// Local fallback translations for GCXP Guest App
// These translations are used when API translations are not available

export type TranslationKey =
  // Common
  | 'common.loading'
  | 'common.error'
  | 'common.submit'
  | 'common.cancel'
  | 'common.confirm'
  | 'common.refresh'
  | 'common.back'
  | 'common.next'
  | 'common.yes'
  | 'common.no'
  | 'common.close'
  | 'common.save'
  | 'common.tryAgain'
  | 'common.sessionExpired'
  | 'common.networkError'
  | 'common.included'
  | 'common.addToCart'
  | 'common.removeFromCart'
  // Breakfast module
  | 'breakfast.title'
  | 'breakfast.subtitle'
  | 'breakfast.orderPlaced'
  | 'breakfast.orderFailed'
  | 'breakfast.viewOrder'
  | 'breakfast.noItems'
  | 'breakfast.cart'
  | 'breakfast.checkout'
  | 'breakfast.categoryEmpty'
  // Order status
  | 'order.title'
  | 'order.order_number'
  | 'order.last_updated'
  | 'order.status.pending'
  | 'order.status.confirmed'
  | 'order.status.preparing'
  | 'order.status.ready'
  | 'order.status.delivered'
  | 'order.status.cancelled'
  | 'order.notFound'
  | 'order.trackOrder'
  // Breakfast Order
  | 'breakfast_order.title'
  | 'breakfast_order.chooseItem'
  | 'breakfast_order.howMany'
  | 'breakfast_order.servings'
  | 'breakfast_order.notesTitle'
  | 'breakfast_order.notesPlaceholder'
  | 'breakfast_order.placeOrder'
  | 'breakfast_order.selectItem'
  | 'breakfast_order.loading'
  | 'breakfast_order.oops'
  | 'breakfast_order.unableLoad'
  | 'breakfast_order.orderFailed'
  | 'breakfast_order.kitchenClosed'
  | 'breakfast_order.closedHours'
  | 'breakfast_order.seeTomorrow'
  | 'breakfast_order.allDone'
  | 'breakfast_order.maxOrdersReached'
  | 'breakfast_order.sending'
  | 'breakfast_order.preparing'
  | 'breakfast_order.yourOrder'
  | 'breakfast_order.ordered'
  | 'breakfast_order.readyPickup'
  | 'breakfast_order.enjoy'
  | 'breakfast_order.table'
  | 'breakfast_order.tableNumber'
  | 'breakfast_order.enterTable'
  | 'breakfast_order.whosOrdering'
  | 'breakfast_order.enterYourName'
  | 'breakfast_order.existingGuests'
  | 'breakfast_order.orNewGuest'
  | 'breakfast_order.slotsAvailable'
  | 'breakfast_order.namesFull'
  | 'breakfast_order.changeName'
  // Welcome page
  | 'welcome.title'
  | 'welcome.roomNumber'
  | 'welcome.guestName'
  | 'welcome.checkIn'
  | 'welcome.checkOut'
  | 'welcome.modules'
  | 'welcome.shareAccess'
  | 'welcome.shareDescription'
  | 'welcome.shareSuccess'
  | 'welcome.additionalDevice'
  | 'welcome.devicesUsed'
  // Session
  | 'session.expired'
  | 'session.invalid'
  | 'session.activating'
  // Errors
  | 'error.networkError'
  | 'error.serverError'
  | 'error.notFound'
  | 'error.unauthorized';

export interface Translations {
  [key: string]: string;
}

// English translations (default)
const en: Translations = {
  // Common
  'common.loading': 'Loading...',
  'common.error': 'An error occurred',
  'common.submit': 'Submit',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.refresh': 'Refresh',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.tryAgain': 'Try Again',
  'common.sessionExpired': 'Your session expired. Please sign in again.',
  'common.networkError': 'Connection problem — check your internet and try again.',
  'common.included': 'Included',
  'common.addToCart': 'Add',
  'common.removeFromCart': 'Remove',
  // Breakfast
  'breakfast.title': 'Breakfast Menu',
  'breakfast.subtitle': 'Choose your breakfast items',
  'breakfast.orderPlaced': 'Order placed successfully!',
  'breakfast.orderFailed': 'Failed to place order',
  'breakfast.viewOrder': 'View Order',
  'breakfast.noItems': 'No items available',
  'breakfast.cart': 'Cart',
  'breakfast.checkout': 'Checkout',
  'breakfast.categoryEmpty': 'No items in this category',
  // Order
  'order.title': 'Order Status',
  'order.order_number': 'Order Number',
  'order.last_updated': 'Last Updated',
  'order.status.pending': 'Pending',
  'order.status.confirmed': 'Confirmed',
  'order.status.preparing': 'Preparing',
  'order.status.ready': 'Ready',
  'order.status.delivered': 'Delivered',
  'order.status.cancelled': 'Cancelled',
  'order.notFound': 'Order not found',
  'order.trackOrder': 'Track Order',
  // Breakfast Order
  'breakfast_order.title': 'Breakfast Order',
  'breakfast_order.chooseItem': 'What would you like?',
  'breakfast_order.howMany': 'How many servings?',
  'breakfast_order.servings': 'servings',
  'breakfast_order.notesTitle': 'Any special requests?',
  'breakfast_order.notesPlaceholder': 'e.g., No onions, extra crispy...',
  'breakfast_order.placeOrder': 'Place Order',
  'breakfast_order.selectItem': 'Select an item to order',
  'breakfast_order.loading': 'Loading breakfast menu...',
  'breakfast_order.oops': 'Oops!',
  'breakfast_order.unableLoad': 'Unable to load breakfast menu',
  'breakfast_order.orderFailed': 'Failed to place order',
  'breakfast_order.kitchenClosed': 'Kitchen Closed',
  'breakfast_order.closedHours': 'Breakfast orders are available from',
  'breakfast_order.seeTomorrow': 'See you tomorrow morning!',
  'breakfast_order.allDone': 'All Done for Today!',
  'breakfast_order.maxOrdersReached': 'Maximum orders reached for today',
  'breakfast_order.sending': 'Sending to Kitchen...',
  'breakfast_order.preparing': 'Your breakfast is being prepared!',
  'breakfast_order.yourOrder': 'Your Order',
  'breakfast_order.ordered': 'Ordered',
  'breakfast_order.readyPickup': 'Your order is ready! Pick up from the breakfast station.',
  'breakfast_order.enjoy': 'Enjoy your meal!',
  'breakfast_order.table': 'Table',
  'breakfast_order.tableNumber': 'Table Number',
  'breakfast_order.enterTable': 'Enter table number',
  'breakfast_order.whosOrdering': 'Who is ordering?',
  'breakfast_order.enterYourName': 'Enter your name',
  'breakfast_order.existingGuests': 'Select your name:',
  'breakfast_order.orNewGuest': 'or add a new guest',
  'breakfast_order.slotsAvailable': 'spots available',
  'breakfast_order.namesFull': 'All guest spots for this room are taken.',
  'breakfast_order.changeName': 'Change',
  // Welcome
  'welcome.title': 'Welcome',
  'welcome.roomNumber': 'Room',
  'welcome.guestName': 'Guest',
  'welcome.checkIn': 'Check-in',
  'welcome.checkOut': 'Check-out',
  'welcome.modules': 'Available Services',
  'welcome.shareAccess': 'Share Access',
  'welcome.shareDescription': 'Share this link with other guests in your room',
  'welcome.shareSuccess': 'Link copied to clipboard!',
  'welcome.additionalDevice': 'Add Another Device',
  'welcome.devicesUsed': 'Devices',
  // Session
  'session.expired': 'Your session has expired',
  'session.invalid': 'Invalid session',
  'session.activating': 'Activating your session...',
  // Errors
  'error.networkError': 'Network error. Please check your connection.',
  'error.serverError': 'Server error. Please try again later.',
  'error.notFound': 'Not found',
  'error.unauthorized': 'Unauthorized access',
  // Module names
  'module.breakfast': 'Breakfast Menu',
  'module.eggs': 'Breakfast Order',
  'module.housekeeping': 'Room Service',
  'module.maintenance': 'Report Issue',
  'module.roomservice': 'Room Service',
  'module.spa': 'Spa & Wellness',
  'module.concierge': 'Concierge',
  'module.minibar': 'Mini Bar',
  'module.guides': 'Guides',
  // Navigation
  'nav.home': 'Home',
  'nav.more': 'More',
  'nav.back': 'Back',
  'nav.allServices': 'All Services',
  'nav.orderStatus': 'Order Status',
  // Housekeeping
  'housekeeping.title': 'Room Service',
  'housekeeping.loading': 'Loading housekeeping services...',
  'housekeeping.unavailable': 'Service Unavailable',
  'housekeeping.unavailableMsg': 'Housekeeping requests are currently unavailable.',
  'housekeeping.contactDesk': 'Please contact the front desk for assistance.',
  'housekeeping.limitReached': 'Daily Limit Reached',
  'housekeeping.tryTomorrow': 'Please try again tomorrow!',
  'housekeeping.submitting': 'Submitting Request...',
  'housekeeping.sendingMsg': 'We\'re sending your request to housekeeping',
  'housekeeping.yourRequest': 'Your Request',
  'housekeeping.onTheWay': 'Items are on the way to your room!',
  'housekeeping.delivered': 'Items delivered! Enjoy your stay!',
  'housekeeping.estimatedDelivery': 'Estimated delivery',
  'housekeeping.makeAnother': 'Make Another Request',
  'housekeeping.viewCart': 'View Cart',
  'housekeeping.specialRequests': 'Special requests (optional)',
  'housekeeping.specialPlaceholder': 'Any special instructions...',
  'housekeeping.submitRequest': 'Submit Request',
  'housekeeping.add': 'Add',
  'housekeeping.room': 'Room',
  'housekeeping.items': 'items',
  'housekeeping.item': 'item',
  'housekeeping.minutes': 'minutes',
  'housekeeping.requested': 'Requested',
  // Maintenance
  'maintenance.title': 'Report an Issue',
  'maintenance.loading': 'Loading maintenance services...',
  'maintenance.unavailable': 'Service Unavailable',
  'maintenance.unavailableMsg': 'Maintenance reporting is currently unavailable.',
  'maintenance.contactDesk': 'Please contact the front desk for assistance.',
  'maintenance.submitting': 'Submitting Report...',
  'maintenance.sendingMsg': 'We\'re sending your report to maintenance',
  'maintenance.myReports': 'My Reports',
  'maintenance.noReports': 'No reports submitted yet',
  'maintenance.reportNew': 'Report New Issue',
  'maintenance.reportAnother': 'Report Another Issue',
  'maintenance.viewAll': 'View All Reports',
  'maintenance.reportStatus': 'Report Status',
  'maintenance.resolved': 'Resolved',
  'maintenance.reportIssue': 'Report Issue',
  'maintenance.subject': 'Subject',
  'maintenance.subjectPlaceholder': 'Brief description of the issue',
  'maintenance.details': 'Details (optional)',
  'maintenance.detailsPlaceholder': 'Provide more details about the issue...',
  'maintenance.location': 'Location (optional)',
  'maintenance.locationPlaceholder': 'e.g., Bathroom, Near the window',
  'maintenance.priority': 'Priority',
  'maintenance.priorityLow': 'Low',
  'maintenance.priorityLowDesc': 'When convenient',
  'maintenance.priorityNormal': 'Normal',
  'maintenance.priorityNormalDesc': 'Standard response',
  'maintenance.priorityHigh': 'High',
  'maintenance.priorityHighDesc': 'Needs prompt attention',
  'maintenance.priorityUrgent': 'Urgent',
  'maintenance.priorityUrgentDesc': 'Immediate attention',
  'maintenance.submitReport': 'Submit Report',
  'maintenance.change': 'Change',
  'maintenance.customQuestion': 'Can\'t find what you\'re looking for?',
  'maintenance.reportCustom': 'Report Custom Issue',
  'maintenance.reported': 'Reported',
  'maintenance.oops': 'Oops!',
  // Welcome page
  'welcome.guests': 'Guests',
  'welcome.yourStay': 'Your Stay',
  'welcome.connectedDevices': 'Connected Devices',
  'welcome.shareDeviceDesc': 'Share access with other guests in your room. Each guest can use their own device.',
  'welcome.addDevice': 'Add Another Device',
  'welcome.processing': 'Processing...',
  'welcome.availableServices': 'Available Services',
  'welcome.guidesDesc': 'Step-by-step guides & information',
  'welcome.shareModalTitle': 'Share Access',
  'welcome.shareModalDesc': 'Share this link with another guest to give them access on their device.',
  'welcome.shareLink': 'Share Link',
  'welcome.copyLink': 'Copy Link',
  'welcome.copied': 'Copied!',
  'welcome.deviceSlots': 'Device slots',
  'welcome.sessionExpired': 'Session Expired',
  'welcome.scanAgain': 'Please scan the QR code again to access services.',
  'welcome.welcome': 'Welcome',
  // ---- auto-added: full guest-app coverage (June 2026) ----
  'activity.active': 'Active Requests',
  'activity.allPeople': 'All',
  'activity.completed': 'Completed',
  'activity.empty': 'No activity yet',
  'activity.emptySub': 'Your requests and orders will appear here',
  'activity.filter.all': 'All',
  'activity.filter.housekeeping': 'Housekeeping',
  'activity.filter.maintenance': 'Maintenance',
  'activity.filter.orders': 'Orders',
  'activity.filter.reservations': 'Reservations',
  'activity.filter.services': 'Services',
  'activity.filter.vouchers': 'Vouchers',
  'activity.title': 'My Activity',
  'activity.vouchers': 'Vouchers',
  'common.category': 'Category',
  'common.date': 'Date',
  'common.hoursAgo': '{{n}}h ago',
  'common.justNow': 'Just now',
  'common.minutesAgo': '{{n}}m ago',
  'common.ok': 'OK',
  'common.oops': 'Oops!',
  'common.priority': 'Priority',
  'common.retry': 'Retry',
  'common.room': 'Room',
  'common.total': 'Total',
  'folio.allCharges': 'Viewing all room charges',
  'folio.balance': 'Balance Due',
  'folio.changeCard': 'Change Card',
  'folio.charged': 'Charged',
  'folio.charges': 'Charges',
  'folio.depositCaptured': 'Deposit Captured',
  'folio.depositHeld': 'Deposit Held',
  'folio.depositReleased': 'Deposit Released',
  'folio.downloadInvoice': 'Download Invoice',
  'folio.empty': 'No charges yet',
  'folio.emptyHint': 'Charges from your orders and services will appear here.',
  'folio.hideHistory': 'Hide History',
  'folio.howToPay': 'How to Pay',
  'folio.noCharges': 'No charges',
  'folio.paid': 'Paid',
  'folio.pay': 'Pay',
  'folio.payAtCounter': 'Pay at Counter',
  'folio.payAtCounterHint': 'Visit the front desk',
  'folio.payHint': 'Pay your bill',
  'folio.payOnline': 'Pay Online',
  'folio.payOnlineHint': 'Pay securely online',
  'folio.paymentSuccess': 'Payment successful',
  'folio.recentCharges': 'Recent Charges',
  'folio.room': 'Room',
  'folio.roomCharges': 'Room Charges',
  'folio.saveCard': 'Save Card',
  'folio.savedForPayments': 'Saved for future payments',
  'folio.securePayment': 'Secure Payment',
  'folio.securityDeposit': 'Security Deposit',
  'folio.settleAll': 'Settle All',
  'folio.settleSuccess': 'Payment settled successfully',
  'folio.settled': 'Settled',
  'folio.payViaHotel': 'Charges are added to your hotel bill. You pay at checkout with the card the hotel has on file, or at the front desk.',
  'folio.statusDue': 'Payment due — please settle at the front desk',
  'folio.paidAtHotel': 'Paid at the hotel',
  'folio.cardRequiredHint': 'Save a payment card to order paid items — they are charged to it when you order.',
  'folio.chargedAtPurchase': 'Paid items are charged to your saved card when you order.',
  'folio.pmsPosted': 'On your hotel bill',
  'folio.pmsPending': 'Adding to your hotel bill',
  'folio.pmsBeingChecked': 'Being checked by the hotel',
  'folio.pmsFrontDesk': 'Settled at the front desk',
  'station_order.saveCard': 'Save a card',
  'folio.showHistory': 'View Activity History',
  'folio.title': 'My Folio',
  'folio.total': 'Total',
  'folio.waitingForTap': 'Waiting for Tap',
  'folio.waitingForTapDesc': 'Hold your card to the reader',
  'footer.dataDeletion': 'Data Deletion',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'guides.finish': 'Finish',
  'guides.guides': 'Guides',
  'guides.iUnderstand': 'I Understand',
  'guides.next': 'Next',
  'guides.nextIn': 'Next In',
  'guides.noCategories': 'No categories',
  'guides.noGuides': 'No guides',
  'guides.previous': 'Previous',
  'guides.steps': 'Steps',
  'guides.subtitle': 'Guides',
  'guides.title': 'Guides',
  'laundry.acknowledgedMsg': 'Acknowledged',
  'laundry.activeOrder': 'Active Order',
  'laundry.add': 'Add',
  'laundry.contactDesk': 'Please contact the front desk',
  'laundry.deliveredMsg': 'Delivered',
  'laundry.each': 'each',
  'laundry.estimatedProcessing': 'Estimated Processing',
  'laundry.estimatedReady': 'Estimated Ready',
  'laundry.expressDelivery': 'Express Delivery',
  'laundry.expressFee': 'Express Fee',
  'laundry.expressMsg': 'Express Service',
  'laundry.history': 'History',
  'laundry.hours': 'hours',
  'laundry.item': 'item',
  'laundry.items': 'items',
  'laundry.limitReached': 'Limit Reached',
  'laundry.newRequest': 'New Request',
  'laundry.noOrders': 'No Orders',
  'laundry.notesPlaceholder': 'Special instructions...',
  'laundry.receivedMsg': 'Received',
  'laundry.room': 'Room',
  'laundry.sendingMsg': 'Sending request...',
  'laundry.sentMsg': 'Sent',
  'laundry.specialInstructions': 'Special Instructions',
  'laundry.submitOrder': 'Submit Order',
  'laundry.editOrder': 'Edit Order',
  'laundry.cancelOrder': 'Cancel Order',
  'laundry.confirmCancel': 'Tap to confirm',
  'laundry.cancelFailed': 'Failed to cancel order',
  'laundry.updateOrder': 'Update Order',
  'laundry.editingOrder': 'Editing your order',
  'laundry.editingNote': 'Adjust the items, then tap Update.',
  'laundry.submitted': 'Submitted',
  'laundry.submitting': 'Submitting...',
  'laundry.subtotal': 'Subtotal',
  'laundry.title': 'Laundry',
  'laundry.total': 'Total',
  'laundry.tryTomorrow': 'Try tomorrow',
  'laundry.unavailable': 'Unavailable',
  'laundry.unavailableMsg': 'Service unavailable',
  'laundry.viewBag': 'View Bag',
  'laundry.viewHistory': 'View History',
  'laundry.yourBag': 'Your Bag',
  'laundry.yourOrder': 'Your Order',
  'maintenance.descriptionTooShort': 'Description must be at least 3 characters',
  'maintenance.room': 'Room',
  'maintenance.subjectRequired': 'Please provide a subject for your report',
  'messages.empty': 'No messages',
  'messages.namePrompt.description': 'This will be used to identify your orders and requests',
  'messages.namePrompt.placeholder': 'Enter your name',
  'messages.namePrompt.submit': 'Submit',
  'messages.namePrompt.title': 'What\'s your name?',
  'messages.placeholder': 'Type a message...',
  'messages.subtitle': 'Messages',
  'messages.title': 'Messages',
  'messages.whatsapp.changeNumber': 'Change Number',
  'messages.whatsapp.checkWhatsApp': 'Check WhatsApp',
  'messages.whatsapp.codeSentViaSms': 'Code sent via SMS',
  'messages.whatsapp.codeSentViaWhatsApp': 'Code sent via WhatsApp',
  'messages.whatsapp.connectDescription': 'Connect your WhatsApp to receive messages',
  'messages.whatsapp.enterCode': 'Enter code',
  'messages.whatsapp.modalTitle': 'Verify WhatsApp',
  'messages.whatsapp.phonePlaceholder': 'Enter your phone number',
  'messages.whatsapp.phoneRequired': 'Phone required',
  'messages.whatsapp.sendVerificationCode': 'Send Verification Code',
  'messages.whatsapp.sendViaSms': 'Send via SMS',
  'messages.whatsapp.skipForNow': 'Skip for Now',
  'messages.whatsapp.useSmsInstead': 'Use SMS instead',
  'messages.whatsapp.useWhatsAppInstead': 'Use WhatsApp instead',
  'messages.whatsapp.verified': 'Verified',
  'messages.whatsapp.verifiedDescription': 'WhatsApp verified',
  'messages.whatsapp.verify': 'Verify',
  'messages.whatsapp.verifyButton': 'Verify',
  'namePrompt.continue': 'Continue',
  'namePrompt.description': 'This will be used to identify your orders',
  'namePrompt.placeholder': 'Enter your name',
  'namePrompt.title': 'What\'s your name?',
  'namePrompt.useBookingName': 'Use booking name:',
  'nav.account': 'Account',
  'documents.mine': 'Me (my room)',
  'documents.forWhom': 'Whose document is this?',
  'documents.moveTo': 'Move to…',
  'documents.moveFailed': 'Could not move the document.',
  'profile.bookedForOthers': 'Booked by you',
  'rooms.shareUnavailable': 'Access to this room isn\'t available right now.',
  'rooms.myTitle': 'My rooms',
  'rooms.myIntro': 'These rooms are on your booking and use your phone or email. Share each room\'s access with the guests staying there, or give a room its own contact so its guest gets their own access and earns their own points.',
  'rooms.noneLeft': 'Every room on your booking has its own guest now.',
  'rooms.otherBooking': 'This room is on another booking with your contact — give it its own contact.',
  'rooms.shareHint': 'Let the guests in this room scan this, or send them the link to open on their own phone.',
  'rooms.copied': 'Link copied',
  'rooms.sendLink': 'Send the link',
  'rooms.share': 'Share access',
  'rooms.shareAfterCheckIn': 'You can share this room\'s access once it is checked in.',
  'rooms.ownContact': 'Give this room its own contact',
  'rooms.bill': 'Bill',
  'rooms.shareText': 'Your access to room {{room}}',
  'rooms.cardTitleManaged': 'Your rooms',
  'rooms.cardBodyManaged': '{{n}} more room(s) on your booking. Share their access with the guests staying there, or give each its own contact so they earn their own points.',
  'rooms.cardButtonManaged': 'My rooms',
  'profile.managedRoom': 'This room is part of {{booker}}\'s booking. To earn points for your stay, ask {{booker}} or the front desk to give this room your own phone number.',
  'rooms.title': 'Register your rooms',
  'rooms.cardTitle': 'Your other rooms',
  'rooms.cardBody': '{{n}} more room(s) on this booking use your phone or email. Tell us who is staying in each, so every room gets its own access.',
  'rooms.cardButton': 'Register your rooms',
  'rooms.room': 'Room',
  'rooms.loadError': 'Could not load your rooms.',
  'rooms.saveError': 'Could not save. Please check the details.',
  'rooms.doneTitle': 'Thank you!',
  'rooms.doneBody': 'Each room now has its own guest. They will receive their own access to the stay.',
  'rooms.none': 'There are no rooms to register.',
  'rooms.verifyFirst': 'Verify your phone number on this device to register your rooms.',
  'rooms.intro': 'These rooms use your phone or email. Enter who is staying in each and their own phone or email, so each room gets its own access.',
  'rooms.guestName': 'Guest\'s name',
  'rooms.noContact': 'No phone or email (for example, a child) — I will look after this room',
  'rooms.save': 'Save',
  'profile.title': 'My profile',
  'profile.loadError': 'Could not load your profile.',
  'profile.joinError': 'Could not join. Please try again.',
  'profile.joinTitle': 'Join {{chain}} Rewards',
  'profile.joinBody': 'Earn {{points}} points for every night you stay with us, at any of our hotels. Your stays and points are kept in your profile.',
  'profile.unavailable': 'Rewards are not available at this property.',
  'profile.verifyFirst': 'Verify your phone number on this device to join.',
  'profile.phone': 'Phone',
  'profile.email': 'Email',
  'profile.marketing': 'Send me offers and news (optional)',
  'profile.consent': 'By joining you agree that we keep your profile, stays and points until you ask us to remove them.',
  'profile.join': 'Join',
  'profile.notNow': 'Not now',
  'profile.points': 'points',
  'profile.memberSince': 'Member since {{date}}',
  'profile.details': 'My details',
  'profile.name': 'Name',
  'profile.stays': 'My stays',
  'profile.noStays': 'No stays yet.',
  'profile.nights': '{{n}} nights',
  'profile.history': 'Points history',
  'profile.noPoints': 'Your points appear here after each stay.',
  'profile.earned': 'Stay',
  'profile.adjusted': 'Adjustment',
  'profile.onReservationOf': 'On {{name}}\'s reservation',
  'nav.activity': 'My Activity',
  'nav.guides': 'Guides',
  'nav.housekeeping': 'Housekeeping',
  'nav.maintenance': 'Maintenance',
  'nav.menu': 'Menu',
  'nav.messages': 'Messages',
  'nav.notifications': 'Notifications',
  'nav.vouchers': 'Vouchers',
  'payment.backToFolio': 'Back to Folio',
  'payment.cancelled': 'Payment Cancelled',
  'payment.cancelledMessage': 'Your payment was cancelled',
  'payment.cardSavedAuto': 'Card saved automatically',
  'payment.failed': 'Payment Failed',
  'payment.failedMessage': 'Your payment failed',
  'payment.success': 'Payment Successful',
  'payment.successMessage': 'Your payment was successful',
  'payment.tryAgain': 'Try Again',
  'station_order.add': 'Add',
  'station_order.outOfStock': 'Out of stock',
  'station_order.allDone': 'All Done',
  'station_order.closed': 'Closed',
  'station_order.closedHours': 'Closed during these hours',
  'station_order.enjoy': 'Enjoy',
  'station_order.enterTable': 'Enter table number',
  'station_order.folioChargeHint': 'Add to room folio',
  'station_order.folioChargeNote': 'This will be charged to your room',
  'station_order.folioChargeTitle': 'Charge to Room',
  'station_order.included': 'Included',
  'station_order.maxOrdersReached': 'Max orders reached',
  'station_order.maxOrdersReachedRoom': 'Max orders for room reached',
  'station_order.nextShift': 'Next Shift',
  'station_order.nextShiftNote': 'Available next shift',
  'station_order.notes': 'Notes',
  'station_order.notesPlaceholder': 'Special requests...',
  'station_order.oops': 'Oops!',
  'station_order.orderFailed': 'Order Failed',
  'station_order.ordered': 'Ordered',
  'station_order.placeAnother': 'Place Another Order',
  'station_order.placeOrder': 'Place Order',
  'station_order.editOrder': 'Edit Order',
  'station_order.cancelOrder': 'Cancel Order',
  'station_order.confirmCancel': 'Tap to confirm',
  'station_order.cancelFailed': 'Failed to cancel order',
  'station_order.updateOrder': 'Update Order',
  'station_order.editingOrder': 'Editing your order',
  'station_order.editingNote': 'Adjust the items, then tap Update Order.',
  'station_order.preparing': 'Preparing',
  'station_order.readyPickup': 'Ready for Pickup',
  'station_order.readyRoom': 'Ready in Room',
  'station_order.readyTable': 'Ready at Table',
  'station_order.selectItems': 'Select Items',
  'station_order.sending': 'Sending',
  'station_order.table': 'Table',
  'station_order.tableNumber': 'Table Number',
  'station_order.thankYou': 'Thank You',
  'station_order.unableLoad': 'Unable to Load',
  'station_order.yourOrder': 'Your Order',
  'status_card.editOrder': 'Edit Order',
  'status_card.express': 'Express',
  'status_card.expressSurcharge': 'Express Surcharge',
  'status_card.guest': 'guest',
  'status_card.guests': 'guests',
  'status_card.hk.itemRequest': 'Item Request',
  'status_card.hk.roomCleaning': 'Room Cleaning',
  'status_card.hk.turndown': 'Turndown Service',
  'status_card.issue': 'Issue',
  'status_card.item': 'Item',
  'status_card.partySize': 'Party Size',
  'status_card.quotedPrice': 'Quoted Price',
  'status_card.reason': 'Reason',
  'status_card.request': 'Request',
  'status_card.scheduled': 'Scheduled',
  'status_card.service': 'Service',
  'status_card.station': 'Station',
  'status_card.step.acknowledged': 'Acknowledged',
  'status_card.step.cancelled': 'Cancelled',
  'status_card.step.completed': 'Completed',
  'status_card.step.confirmed': 'Confirmed',
  'status_card.step.declined': 'Declined',
  'status_card.step.delivered': 'Delivered',
  'status_card.step.inProgress': 'In Progress',
  'status_card.step.pending': 'Pending',
  'status_card.step.preparing': 'Preparing',
  'status_card.step.ready': 'Ready',
  'status_card.step.received': 'Received',
  'status_card.step.reported': 'Reported',
  'status_card.step.resolved': 'Resolved',
  'status_card.step.sent': 'Sent',
  'status_card.step.started': 'Started',
  'status_card.summary.laundry': 'Laundry',
  'status_card.summary.maintenance': 'Maintenance',
  'status_card.summary.order': 'Order',
  'status_card.summary.request': 'Request',
  'status_card.summary.reservation': 'Reservation',
  'status_card.summary.service': 'Service Request',
  'status_card.svc.concierge': 'Concierge',
  'status_card.svc.informationRequest': 'Information Request',
  'status_card.svc.luggage': 'Luggage Assistance',
  'status_card.svc.other': 'Other',
  'status_card.svc.roomService': 'Room Service',
  'status_card.svc.specialRequest': 'Special Request',
  'status_card.svc.taxi': 'Taxi / Ride',
  'status_card.svc.transport': 'Transportation',
  'status_card.svc.wakeUpCall': 'Wake-Up Call',
  'status_card.type': 'Type',
  'status_card.type.housekeeping': 'Housekeeping',
  'status_card.type.laundry': 'Laundry',
  'status_card.type.maintenance': 'Maintenance',
  'status_card.type.order': 'Food Order',
  'status_card.type.reservation': 'Reservation',
  'status_card.type.service': 'Service Request',
  'voucher.expires': 'Expires',
  'voucher.fixedValue': '{{amount}} value',
  'voucher.freeItem': 'Free item',
  'voucher.percentOff': '{{amount}}% off',
  'voucher.redeemed': 'Redeemed',
  'voucher.statusActive': 'Active',
  'voucher.statusExpired': 'Expired',
  'voucher.statusFullyUsed': 'Fully Used',
  'voucher.usedCount': '{{used}} / {{max}} used',
  'vouchers.available': 'Available',
  'vouchers.expires': 'Expires',
  'vouchers.noVouchers': 'No Vouchers',
  'vouchers.noVouchersDesc': 'No vouchers available',
  'vouchers.subtitle': 'My Vouchers',
  'vouchers.title': 'My Vouchers',
  'vouchers.used': 'Used / Expired',
  'vouchers.uses': 'Uses',
  'vouchers.validAt': 'Valid at',
  'welcome.dayPass': 'Day Pass',
  'welcome.event': 'Event',
  'welcome.lounge': 'Lounge',
  'welcome.orderNow': 'Tap to view menu & order',
  'welcome.ordering': 'Ordering',
  'welcome.poolbar': 'Pool Bar',
  'welcome.services': 'Services',
  'welcome.sessionLabel': 'Session',
  'welcome.spaSession': 'Session',
  'welcome.table': 'Table',
  'welcome.verifyDesc': 'Verify your phone to unlock folio charges and allow additional devices to session.',
  'welcome.verifyNow': 'Verify',
  'welcome.verifyRoom': 'Verify Your Room',

  // One session per stay: pre-arrival + post-stay modules
  'common.saving': 'Saving…',
  'common.continue': 'Continue',
  'common.backHome': 'Back to home',
  'common.done': 'Done',
  'module.registration': 'Registration',
  'module.documents': 'Documents',
  'module.review': 'Review',
  'module.lost_property': 'Lost property',
  'welcome.registrationDesc': 'Your details & signature before arrival',
  'welcome.documentsDesc': 'Upload your passport or ID',
  'welcome.beforeYouArrive': 'Before you arrive',
  'welcome.arrivingOn': 'Arriving',
  'welcome.registrationDone': 'Your registration is complete. See you soon!',
  'welcome.completeRegistration': 'Complete your registration',
  'welcome.reviewDesc': 'Tell us how your stay was',
  'welcome.lostPropertyDesc': 'Left something behind?',
  'welcome.thanksForStaying': 'Thank you for staying with us',
  'welcome.postStayHint': 'Your invoice, messages and lost property are here for a few more days.',
  'welcome.rateStay': 'Rate your stay',
  'registration.title': 'Registration',
  'registration.subtitle': 'Your details for the stay',
  'registration.nameRequired': 'Please enter your first and last name.',
  'registration.saveFailed': 'Could not save your details.',
  'registration.acceptTerms': 'Please accept the terms to continue.',
  'registration.confirmFailed': 'Could not complete your registration.',
  'registration.firstName': 'First name',
  'registration.lastName': 'Last name',
  'registration.name': 'Name',
  'registration.email': 'Email',
  'registration.phone': 'Phone',
  'registration.country': 'Country',
  'registration.passportId': 'Passport / ID',
  'registration.dateOfBirth': 'Date of birth',
  'registration.party': 'Guests',
  'registration.adults': 'Adults',
  'registration.children': 'Children',
  'registration.babies': 'Babies',
  'registration.signature': 'Signature (type your full name)',
  'registration.terms': 'I confirm the details above are correct and I accept the property\'s terms and conditions.',
  'registration.complete': 'Complete registration',
  'registration.doneTitle': 'You\'re all set',
  'registration.doneText': 'Your registration is complete. We\'ll have everything ready for your arrival.',
  'registration.addDocuments': 'Upload documents',
  'registration.edit': 'Edit my details',
  'documents.title': 'Documents',
  'documents.subtitle': 'Passport or ID for everyone registering',
  'documents.hint': 'JPG, PNG or PDF, up to 15 MB.',
  'documents.passport': 'Passport',
  'documents.idCard': 'ID card',
  'documents.visa': 'Visa',
  'documents.other': 'Other',
  'documents.add': 'Add file',
  'documents.uploading': 'Uploading…',
  'documents.uploadFailed': 'Upload failed.',
  'documents.removeFailed': 'Could not remove the document.',
  'documents.none': 'No documents uploaded yet.',
  'documents.verified': 'verified',
  'documents.view': 'View',
  'documents.locked': 'locked',
  'documents.remove': 'Remove',
  'review.title': 'Rate your stay',
  'review.subtitle': 'Thank you for staying with us',
  'review.overall': 'Overall',
  'review.staff': 'Staff',
  'review.cleanliness': 'Cleanliness',
  'review.comment': 'Anything you would like to tell us?',
  'review.send': 'Send review',
  'review.update': 'Update review',
  'review.thanks': 'Thank you! Your review has been sent.',
  'review.empty': 'Please give a rating or a comment.',
  'review.failed': 'Could not send your review. Please try again.',
  'lostProperty.title': 'Lost property',
  'lostProperty.subtitle': 'Left something behind? Tell us',
  'lostProperty.item': 'What did you leave?',
  'lostProperty.description': 'Description (colour, brand…)',
  'lostProperty.where': 'Where do you think you left it?',
  'lostProperty.contact': 'Best way to reach you (optional)',
  'lostProperty.send': 'Send report',
  'lostProperty.itemRequired': 'Please tell us what you left behind.',
  'lostProperty.failed': 'Could not send your report. Please try again.',
  'lostProperty.sentTitle': 'We got your report',
  'lostProperty.sentText': 'We\'ll look for it and reply in Messages.',
  'lostProperty.openMessages': 'Open messages',
};

// Hebrew translations
const he: Translations = {
  // Common
  'common.loading': 'טוען...',
  'common.error': 'אירעה שגיאה',
  'common.submit': 'שלח',
  'common.cancel': 'ביטול',
  'common.confirm': 'אישור',
  'common.refresh': 'רענון',
  'common.back': 'חזרה',
  'common.next': 'הבא',
  'common.yes': 'כן',
  'common.no': 'לא',
  'common.close': 'סגור',
  'common.save': 'שמור',
  'common.tryAgain': 'נסה שוב',
  'common.sessionExpired': 'תוקף החיבור פג. יש להתחבר מחדש.',
  'common.networkError': 'בעיית תקשורת — בדקו את החיבור לאינטרנט ונסו שוב.',
  'common.included': 'כלול',
  'common.addToCart': 'הוסף',
  'common.removeFromCart': 'הסר',
  // Breakfast
  'breakfast.title': 'תפריט ארוחת בוקר',
  'breakfast.subtitle': 'בחר את פריטי ארוחת הבוקר שלך',
  'breakfast.orderPlaced': 'ההזמנה בוצעה בהצלחה!',
  'breakfast.orderFailed': 'שגיאה בביצוע ההזמנה',
  'breakfast.viewOrder': 'צפה בהזמנה',
  'breakfast.noItems': 'אין פריטים זמינים',
  'breakfast.cart': 'סל קניות',
  'breakfast.checkout': 'לתשלום',
  'breakfast.categoryEmpty': 'אין פריטים בקטגוריה זו',
  // Order
  'order.title': 'סטטוס הזמנה',
  'order.order_number': 'מספר הזמנה',
  'order.last_updated': 'עודכן לאחרונה',
  'order.status.pending': 'ממתין',
  'order.status.confirmed': 'אושר',
  'order.status.preparing': 'בהכנה',
  'order.status.ready': 'מוכן',
  'order.status.delivered': 'נמסר',
  'order.status.cancelled': 'בוטל',
  'order.notFound': 'ההזמנה לא נמצאה',
  'order.trackOrder': 'עקוב אחר הזמנה',
  // Breakfast Order
  'breakfast_order.title': 'הזמנת ארוחת בוקר',
  'breakfast_order.chooseItem': 'מה תרצה?',
  'breakfast_order.howMany': 'כמה מנות?',
  'breakfast_order.servings': 'מנות',
  'breakfast_order.notesTitle': 'בקשות מיוחדות?',
  'breakfast_order.notesPlaceholder': 'לדוגמא: בלי בצל, פריך יותר...',
  'breakfast_order.placeOrder': 'בצע הזמנה',
  'breakfast_order.selectItem': 'בחר פריט להזמנה',
  'breakfast_order.loading': 'טוען תפריט ארוחת בוקר...',
  'breakfast_order.oops': 'אופס!',
  'breakfast_order.unableLoad': 'לא ניתן לטעון תפריט ארוחת בוקר',
  'breakfast_order.orderFailed': 'שגיאה בביצוע ההזמנה',
  'breakfast_order.kitchenClosed': 'המטבח סגור',
  'breakfast_order.closedHours': 'הזמנות ארוחת בוקר זמינות מ',
  'breakfast_order.seeTomorrow': 'נתראה מחר בבוקר!',
  'breakfast_order.allDone': 'סיימת להיום!',
  'breakfast_order.maxOrdersReached': 'הגעת למספר המקסימלי של הזמנות להיום',
  'breakfast_order.sending': 'שולח למטבח...',
  'breakfast_order.preparing': 'ארוחת הבוקר שלך בהכנה!',
  'breakfast_order.yourOrder': 'ההזמנה שלך',
  'breakfast_order.ordered': 'הוזמן',
  'breakfast_order.readyPickup': 'ההזמנה שלך מוכנה! אסוף מעמדת ארוחת הבוקר.',
  'breakfast_order.enjoy': 'בתאבון!',
  'breakfast_order.table': 'שולחן',
  'breakfast_order.tableNumber': 'מספר שולחן',
  'breakfast_order.enterTable': 'הכנס מספר שולחן',
  'breakfast_order.whosOrdering': 'מי מזמין?',
  'breakfast_order.enterYourName': 'הכנס את שמך',
  'breakfast_order.existingGuests': 'בחר את שמך:',
  'breakfast_order.orNewGuest': 'או הוסף אורח חדש',
  'breakfast_order.slotsAvailable': 'מקומות פנויים',
  'breakfast_order.namesFull': 'כל מקומות האורחים בחדר זה תפוסים.',
  'breakfast_order.changeName': 'שנה',
  // Welcome
  'welcome.title': 'ברוכים הבאים',
  'welcome.roomNumber': 'חדר',
  'welcome.guestName': 'אורח',
  'welcome.checkIn': 'צ\'ק-אין',
  'welcome.checkOut': 'צ\'ק-אאוט',
  'welcome.modules': 'שירותים זמינים',
  'welcome.shareAccess': 'שתף גישה',
  'welcome.shareDescription': 'שתף קישור זה עם אורחים אחרים בחדר',
  'welcome.shareSuccess': 'הקישור הועתק!',
  'welcome.additionalDevice': 'הוסף מכשיר נוסף',
  'welcome.devicesUsed': 'מכשירים',
  // Session
  'session.expired': 'פג תוקף הסשן שלך',
  'session.invalid': 'סשן לא תקין',
  'session.activating': 'מפעיל את הסשן שלך...',
  // Errors
  'error.networkError': 'שגיאת רשת. אנא בדוק את החיבור שלך.',
  'error.serverError': 'שגיאת שרת. אנא נסה שוב מאוחר יותר.',
  'error.notFound': 'לא נמצא',
  'error.unauthorized': 'גישה לא מורשית',
  // Module names
  'module.breakfast': 'תפריט ארוחת בוקר',
  'module.eggs': 'הזמנת ארוחת בוקר',
  'module.housekeeping': 'שירות חדרים',
  'module.maintenance': 'דיווח תקלה',
  'module.roomservice': 'שירות חדרים',
  'module.spa': 'ספא ובריאות',
  'module.concierge': 'קונסיירז\'',
  'module.minibar': 'מיני בר',
  'module.guides': 'מדריכים',
  // Navigation
  'nav.home': 'בית',
  'nav.more': 'עוד',
  'nav.back': 'חזרה',
  'nav.allServices': 'כל השירותים',
  'nav.orderStatus': 'סטטוס הזמנה',
  // Housekeeping
  'housekeeping.title': 'שירות חדרים',
  'housekeeping.loading': 'טוען שירותי חדרים...',
  'housekeeping.unavailable': 'שירות לא זמין',
  'housekeeping.unavailableMsg': 'בקשות שירות חדרים אינן זמינות כרגע.',
  'housekeeping.contactDesk': 'אנא פנה לדלפק הקבלה לעזרה.',
  'housekeeping.limitReached': 'הגעת למגבלה היומית',
  'housekeeping.tryTomorrow': 'אנא נסה שוב מחר!',
  'housekeeping.submitting': 'שולח בקשה...',
  'housekeeping.sendingMsg': 'שולחים את הבקשה שלך לשירות החדרים',
  'housekeeping.yourRequest': 'הבקשה שלך',
  'housekeeping.onTheWay': 'הפריטים בדרך לחדר שלך!',
  'housekeeping.delivered': 'הפריטים נמסרו! נעים להכיר!',
  'housekeeping.estimatedDelivery': 'משלוח משוער',
  'housekeeping.makeAnother': 'בקשה נוספת',
  'housekeeping.viewCart': 'צפה בסל',
  'housekeeping.specialRequests': 'בקשות מיוחדות (אופציונלי)',
  'housekeeping.specialPlaceholder': 'הוראות מיוחדות...',
  'housekeeping.submitRequest': 'שלח בקשה',
  'housekeeping.add': 'הוסף',
  'housekeeping.room': 'חדר',
  'housekeeping.items': 'פריטים',
  'housekeeping.item': 'פריט',
  'housekeeping.minutes': 'דקות',
  'housekeeping.requested': 'התבקש',
  // Maintenance
  'maintenance.title': 'דיווח תקלה',
  'maintenance.loading': 'טוען שירותי תחזוקה...',
  'maintenance.unavailable': 'שירות לא זמין',
  'maintenance.unavailableMsg': 'דיווח תקלות אינו זמין כרגע.',
  'maintenance.contactDesk': 'אנא פנה לדלפק הקבלה לעזרה.',
  'maintenance.submitting': 'שולח דיווח...',
  'maintenance.sendingMsg': 'שולחים את הדיווח שלך לתחזוקה',
  'maintenance.myReports': 'הדיווחים שלי',
  'maintenance.noReports': 'עדיין לא הוגשו דיווחים',
  'maintenance.reportNew': 'דווח תקלה חדשה',
  'maintenance.reportAnother': 'דווח תקלה נוספת',
  'maintenance.viewAll': 'צפה בכל הדיווחים',
  'maintenance.reportStatus': 'סטטוס דיווח',
  'maintenance.resolved': 'נפתר',
  'maintenance.reportIssue': 'דווח תקלה',
  'maintenance.subject': 'נושא',
  'maintenance.subjectPlaceholder': 'תיאור קצר של הבעיה',
  'maintenance.details': 'פרטים (אופציונלי)',
  'maintenance.detailsPlaceholder': 'ספק פרטים נוספים על הבעיה...',
  'maintenance.location': 'מיקום (אופציונלי)',
  'maintenance.locationPlaceholder': 'לדוגמא: חדר אמבטיה, ליד החלון',
  'maintenance.priority': 'עדיפות',
  'maintenance.priorityLow': 'נמוכה',
  'maintenance.priorityLowDesc': 'כשנוח',
  'maintenance.priorityNormal': 'רגילה',
  'maintenance.priorityNormalDesc': 'תגובה רגילה',
  'maintenance.priorityHigh': 'גבוהה',
  'maintenance.priorityHighDesc': 'דורש טיפול מהיר',
  'maintenance.priorityUrgent': 'דחוף',
  'maintenance.priorityUrgentDesc': 'דורש טיפול מיידי',
  'maintenance.submitReport': 'שלח דיווח',
  'maintenance.change': 'שנה',
  'maintenance.customQuestion': 'לא מצאת את מה שחיפשת?',
  'maintenance.reportCustom': 'דווח תקלה מותאמת',
  'maintenance.reported': 'דווח',
  'maintenance.oops': 'אופס!',
  // Welcome page
  'welcome.guests': 'אורחים',
  'welcome.yourStay': 'השהייה שלך',
  'welcome.connectedDevices': 'מכשירים מחוברים',
  'welcome.shareDeviceDesc': 'שתפו גישה עם אורחים אחרים בחדר. כל אורח יכול להשתמש במכשיר שלו.',
  'welcome.guidesDesc': 'מדריכים שלב אחר שלב ומידע',
  'welcome.addDevice': 'הוסף מכשיר נוסף',
  'welcome.processing': 'מעבד...',
  'welcome.availableServices': 'שירותים זמינים',
  'welcome.shareModalTitle': 'שתף גישה',
  'welcome.shareModalDesc': 'שתף קישור זה עם אורח אחר כדי לתת לו גישה במכשיר שלו.',
  'welcome.shareLink': 'שתף קישור',
  'welcome.copyLink': 'העתק קישור',
  'welcome.copied': 'הועתק!',
  'welcome.deviceSlots': 'חריצי מכשירים',
  'welcome.sessionExpired': 'פג תוקף',
  'welcome.scanAgain': 'אנא סרוק את קוד ה-QR שוב כדי לגשת לשירותים.',
  'welcome.welcome': 'ברוכים הבאים',
  // ---- auto-added: full guest-app coverage (June 2026) ----
  'activity.active': 'בקשות פעילות',
  'activity.allPeople': 'הכל',
  'activity.completed': 'הושלם',
  'activity.empty': 'אין פעילות עדיין',
  'activity.emptySub': 'הבקשות וההזמנות שלך יופיעו כאן',
  'activity.filter.all': 'הכל',
  'activity.filter.housekeeping': 'שירות חדרים',
  'activity.filter.maintenance': 'תחזוקה',
  'activity.filter.orders': 'הזמנות',
  'activity.filter.reservations': 'הזמנות מקום',
  'activity.filter.services': 'שירותים',
  'activity.filter.vouchers': 'שוברים',
  'activity.title': 'הפעילות שלי',
  'activity.vouchers': 'שוברים',
  'common.category': 'קטגוריה',
  'common.date': 'תאריך',
  'common.hoursAgo': 'לפני {{n}} שע\'',
  'common.justNow': 'הרגע',
  'common.minutesAgo': 'לפני {{n}} דק\'',
  'common.ok': 'אישור',
  'common.oops': 'אופס!',
  'common.priority': 'עדיפות',
  'common.retry': 'נסה שוב',
  'common.room': 'חדר',
  'common.total': 'סה"כ',
  'folio.allCharges': 'צפייה בכל חיובי החדר',
  'folio.balance': 'יתרה לתשלום',
  'folio.changeCard': 'שנה כרטיס',
  'folio.charged': 'חויב',
  'folio.charges': 'חיובים',
  'folio.depositCaptured': 'פיקדון נגבה',
  'folio.depositHeld': 'פיקדון מוקפא',
  'folio.depositReleased': 'פיקדון שוחרר',
  'folio.downloadInvoice': 'הורד חשבונית',
  'folio.empty': 'אין חיובים עדיין',
  'folio.emptyHint': 'חיובים מהזמנות ושירותים יופיעו כאן.',
  'folio.hideHistory': 'הסתר היסטוריה',
  'folio.howToPay': 'איך לשלם',
  'folio.noCharges': 'אין חיובים',
  'folio.paid': 'שולם',
  'folio.pay': 'שלם',
  'folio.payAtCounter': 'תשלום בדלפק',
  'folio.payAtCounterHint': 'גש לדלפק הקבלה',
  'folio.payHint': 'שלם את החשבון שלך',
  'folio.payOnline': 'תשלום אונליין',
  'folio.payOnlineHint': 'שלם בצורה מאובטחת אונליין',
  'folio.paymentSuccess': 'התשלום בוצע בהצלחה',
  'folio.recentCharges': 'חיובים אחרונים',
  'folio.room': 'חדר',
  'folio.roomCharges': 'חיובי חדר',
  'folio.saveCard': 'שמור כרטיס',
  'folio.savedForPayments': 'נשמר לתשלומים עתידיים',
  'folio.securePayment': 'תשלום מאובטח',
  'folio.securityDeposit': 'פיקדון ביטחון',
  'folio.settleAll': 'סלק הכל',
  'folio.settleSuccess': 'התשלום סולק בהצלחה',
  'folio.settled': 'סולק',
  'folio.payViaHotel': 'החיובים נוספים לחשבון המלון שלך. התשלום מתבצע בצ\'ק-אאוט בכרטיס שבידי המלון, או בקבלה.',
  'folio.statusDue': 'יתרה לתשלום — נא להסדיר בקבלה',
  'folio.paidAtHotel': 'שולם במלון',
  'folio.cardRequiredHint': 'שמרו כרטיס תשלום כדי להזמין פריטים בתשלום — החיוב מתבצע בכרטיס בעת ההזמנה.',
  'folio.chargedAtPurchase': 'פריטים בתשלום מחויבים בכרטיס השמור בעת ההזמנה.',
  'folio.pmsPosted': 'בחשבון המלון שלך',
  'folio.pmsPending': 'נוסף לחשבון המלון שלך',
  'folio.pmsBeingChecked': 'בבדיקת המלון',
  'folio.pmsFrontDesk': 'הוסדר בקבלה',
  'station_order.saveCard': 'שמירת כרטיס',
  'folio.showHistory': 'צפייה בהיסטוריית פעילות',
  'folio.title': 'החשבון שלי',
  'folio.total': 'סה"כ',
  'folio.waitingForTap': 'ממתין להצמדה',
  'folio.waitingForTapDesc': 'הצמד את הכרטיס לקורא',
  'footer.dataDeletion': 'מחיקת נתונים',
  'footer.privacy': 'מדיניות פרטיות',
  'footer.terms': 'תנאי שימוש',
  'guides.finish': 'סיום',
  'guides.guides': 'מדריכים',
  'guides.iUnderstand': 'הבנתי',
  'guides.next': 'הבא',
  'guides.nextIn': 'הבא בעוד',
  'guides.noCategories': 'אין קטגוריות',
  'guides.noGuides': 'אין מדריכים',
  'guides.previous': 'הקודם',
  'guides.steps': 'שלבים',
  'guides.subtitle': 'מדריכים',
  'guides.title': 'מדריכים',
  'laundry.acknowledgedMsg': 'התקבל',
  'laundry.activeOrder': 'הזמנה פעילה',
  'laundry.add': 'הוסף',
  'laundry.contactDesk': 'אנא פנה לדלפק הקבלה',
  'laundry.deliveredMsg': 'נמסר',
  'laundry.each': 'ליחידה',
  'laundry.estimatedProcessing': 'זמן עיבוד משוער',
  'laundry.estimatedReady': 'מוכן משוער',
  'laundry.expressDelivery': 'משלוח אקספרס',
  'laundry.expressFee': 'דמי אקספרס',
  'laundry.expressMsg': 'שירות אקספרס',
  'laundry.history': 'היסטוריה',
  'laundry.hours': 'שעות',
  'laundry.item': 'פריט',
  'laundry.items': 'פריטים',
  'laundry.limitReached': 'הגעת למגבלה',
  'laundry.newRequest': 'בקשה חדשה',
  'laundry.noOrders': 'אין הזמנות',
  'laundry.notesPlaceholder': 'הוראות מיוחדות...',
  'laundry.receivedMsg': 'התקבל',
  'laundry.room': 'חדר',
  'laundry.sendingMsg': 'שולח בקשה...',
  'laundry.sentMsg': 'נשלח',
  'laundry.specialInstructions': 'הוראות מיוחדות',
  'laundry.submitOrder': 'שלח הזמנה',
  'laundry.editOrder': 'עריכת הזמנה',
  'laundry.cancelOrder': 'ביטול הזמנה',
  'laundry.confirmCancel': 'להקשה לאישור',
  'laundry.cancelFailed': 'ביטול ההזמנה נכשל',
  'laundry.updateOrder': 'עדכון הזמנה',
  'laundry.editingOrder': 'עריכת ההזמנה שלך',
  'laundry.editingNote': 'עדכנו את הפריטים ולחצו על עדכון.',
  'laundry.submitted': 'נשלח',
  'laundry.submitting': 'שולח...',
  'laundry.subtotal': 'סכום ביניים',
  'laundry.title': 'כביסה',
  'laundry.total': 'סה"כ',
  'laundry.tryTomorrow': 'נסה מחר',
  'laundry.unavailable': 'לא זמין',
  'laundry.unavailableMsg': 'השירות אינו זמין',
  'laundry.viewBag': 'צפה בשקית',
  'laundry.viewHistory': 'צפה בהיסטוריה',
  'laundry.yourBag': 'השקית שלך',
  'laundry.yourOrder': 'ההזמנה שלך',
  'maintenance.descriptionTooShort': 'התיאור חייב להכיל לפחות 3 תווים',
  'maintenance.room': 'חדר',
  'maintenance.subjectRequired': 'אנא ציין נושא לדיווח שלך',
  'messages.empty': 'אין הודעות',
  'messages.namePrompt.description': 'זה ישמש לזיהוי ההזמנות והבקשות שלך',
  'messages.namePrompt.placeholder': 'הכנס את שמך',
  'messages.namePrompt.submit': 'שלח',
  'messages.namePrompt.title': 'מה שמך?',
  'messages.placeholder': 'הקלד הודעה...',
  'messages.subtitle': 'הודעות',
  'messages.title': 'הודעות',
  'messages.whatsapp.changeNumber': 'שנה מספר',
  'messages.whatsapp.checkWhatsApp': 'בדוק את WhatsApp',
  'messages.whatsapp.codeSentViaSms': 'הקוד נשלח ב-SMS',
  'messages.whatsapp.codeSentViaWhatsApp': 'הקוד נשלח ב-WhatsApp',
  'messages.whatsapp.connectDescription': 'חבר את WhatsApp שלך כדי לקבל הודעות',
  'messages.whatsapp.enterCode': 'הכנס קוד',
  'messages.whatsapp.modalTitle': 'אימות WhatsApp',
  'messages.whatsapp.phonePlaceholder': 'הכנס את מספר הטלפון שלך',
  'messages.whatsapp.phoneRequired': 'נדרש מספר טלפון',
  'messages.whatsapp.sendVerificationCode': 'שלח קוד אימות',
  'messages.whatsapp.sendViaSms': 'שלח ב-SMS',
  'messages.whatsapp.skipForNow': 'דלג לעת עתה',
  'messages.whatsapp.useSmsInstead': 'השתמש ב-SMS במקום',
  'messages.whatsapp.useWhatsAppInstead': 'השתמש ב-WhatsApp במקום',
  'messages.whatsapp.verified': 'מאומת',
  'messages.whatsapp.verifiedDescription': 'WhatsApp אומת',
  'messages.whatsapp.verify': 'אמת',
  'messages.whatsapp.verifyButton': 'אמת',
  'namePrompt.continue': 'המשך',
  'namePrompt.description': 'זה ישמש לזיהוי ההזמנות שלך',
  'namePrompt.placeholder': 'הכנס את שמך',
  'namePrompt.title': 'מה שמך?',
  'namePrompt.useBookingName': 'השתמש בשם מההזמנה:',
  'nav.account': 'חשבון',
  'documents.mine': 'אני (החדר שלי)',
  'documents.forWhom': 'של מי המסמך?',
  'documents.moveTo': 'העברה ל…',
  'documents.moveFailed': 'לא הצלחנו להעביר את המסמך.',
  'profile.bookedForOthers': 'הוזמנו על ידך',
  'rooms.shareUnavailable': 'הגישה לחדר הזה לא זמינה כרגע.',
  'rooms.myTitle': 'החדרים שלי',
  'rooms.myIntro': 'החדרים האלה בהזמנה שלכם ורשומים עם הטלפון או האימייל שלכם. שתפו את הגישה לכל חדר עם האורחים שבו, או רשמו לחדר פרטי קשר משלו כדי שהאורח יקבל גישה משלו ויצבור נקודות משלו.',
  'rooms.noneLeft': 'לכל חדר בהזמנה יש עכשיו אורח משלו.',
  'rooms.otherBooking': 'החדר הזה בהזמנה אחרת עם פרטי הקשר שלכם — רשמו לו פרטי קשר משלו.',
  'rooms.shareHint': 'תנו לאורחים בחדר לסרוק את הקוד, או שלחו להם את הקישור לפתיחה בטלפון שלהם.',
  'rooms.copied': 'הקישור הועתק',
  'rooms.sendLink': 'שליחת הקישור',
  'rooms.share': 'שיתוף גישה',
  'rooms.shareAfterCheckIn': 'אפשר לשתף את הגישה לחדר אחרי הצ׳ק-אין.',
  'rooms.ownContact': 'פרטי קשר משלו לחדר',
  'rooms.bill': 'חשבון',
  'rooms.shareText': 'הגישה שלך לחדר {{room}}',
  'rooms.cardTitleManaged': 'החדרים שלכם',
  'rooms.cardBodyManaged': 'עוד {{n}} חדרים בהזמנה שלכם. שתפו את הגישה עם האורחים שבהם, או רשמו לכל חדר פרטי קשר משלו כדי שיצברו נקודות משלהם.',
  'rooms.cardButtonManaged': 'החדרים שלי',
  'profile.managedRoom': 'החדר הזה הוא חלק מההזמנה של {{booker}}. כדי לצבור נקודות על השהייה, בקשו מ{{booker}} או מהקבלה לרשום לחדר את מספר הטלפון שלכם.',
  'rooms.title': 'רישום החדרים שלכם',
  'rooms.cardTitle': 'החדרים הנוספים שלכם',
  'rooms.cardBody': 'עוד {{n}} חדרים בהזמנה זו רשומים עם הטלפון או האימייל שלכם. ספרו לנו מי שוהה בכל חדר, כדי שלכל חדר תהיה גישה משלו.',
  'rooms.cardButton': 'רישום החדרים',
  'rooms.room': 'חדר',
  'rooms.loadError': 'לא הצלחנו לטעון את החדרים.',
  'rooms.saveError': 'השמירה נכשלה. בדקו את הפרטים.',
  'rooms.doneTitle': 'תודה!',
  'rooms.doneBody': 'לכל חדר יש עכשיו אורח משלו. הם יקבלו גישה משלהם לשהייה.',
  'rooms.none': 'אין חדרים לרישום.',
  'rooms.verifyFirst': 'אמתו את מספר הטלפון במכשיר זה כדי לרשום את החדרים.',
  'rooms.intro': 'החדרים האלה רשומים עם הטלפון או האימייל שלכם. הזינו מי שוהה בכל חדר ואת הטלפון או האימייל שלו, כדי שלכל חדר תהיה גישה משלו.',
  'rooms.guestName': 'שם האורח',
  'rooms.noContact': 'בלי טלפון או אימייל (למשל ילד) — אני אדאג לחדר הזה',
  'rooms.save': 'שמירה',
  'profile.title': 'הפרופיל שלי',
  'profile.loadError': 'לא הצלחנו לטעון את הפרופיל.',
  'profile.joinError': 'ההצטרפות נכשלה. נסו שוב.',
  'profile.joinTitle': 'הצטרפו למועדון {{chain}}',
  'profile.joinBody': 'צברו {{points}} נקודות על כל לילה אצלנו, בכל אחד מהמלונות שלנו. השהיות והנקודות נשמרות בפרופיל שלכם.',
  'profile.unavailable': 'מועדון הלקוחות אינו זמין במלון זה.',
  'profile.verifyFirst': 'אמתו את מספר הטלפון במכשיר זה כדי להצטרף.',
  'profile.phone': 'טלפון',
  'profile.email': 'אימייל',
  'profile.marketing': 'שלחו לי הטבות ועדכונים (לא חובה)',
  'profile.consent': 'בהצטרפות אתם מסכימים שנשמור את הפרופיל, השהיות והנקודות עד שתבקשו למחוק אותם.',
  'profile.join': 'הצטרפות',
  'profile.notNow': 'לא עכשיו',
  'profile.points': 'נקודות',
  'profile.memberSince': 'חבר/ה מאז {{date}}',
  'profile.details': 'הפרטים שלי',
  'profile.name': 'שם',
  'profile.stays': 'השהיות שלי',
  'profile.noStays': 'עדיין אין שהיות.',
  'profile.nights': '{{n}} לילות',
  'profile.history': 'היסטוריית נקודות',
  'profile.noPoints': 'הנקודות יופיעו כאן אחרי כל שהייה.',
  'profile.earned': 'שהייה',
  'profile.adjusted': 'עדכון',
  'profile.onReservationOf': 'בהזמנה של {{name}}',
  'nav.activity': 'הפעילות שלי',
  'nav.guides': 'מדריכים',
  'nav.housekeeping': 'שירות חדרים',
  'nav.maintenance': 'תחזוקה',
  'nav.menu': 'תפריט',
  'nav.messages': 'הודעות',
  'nav.notifications': 'התראות',
  'nav.vouchers': 'שוברים',
  'payment.backToFolio': 'חזרה לחשבון',
  'payment.cancelled': 'התשלום בוטל',
  'payment.cancelledMessage': 'התשלום שלך בוטל',
  'payment.cardSavedAuto': 'הכרטיס נשמר אוטומטית',
  'payment.failed': 'התשלום נכשל',
  'payment.failedMessage': 'התשלום שלך נכשל',
  'payment.success': 'התשלום בוצע בהצלחה',
  'payment.successMessage': 'התשלום שלך בוצע בהצלחה',
  'payment.tryAgain': 'נסה שוב',
  'station_order.add': 'הוסף',
  'station_order.outOfStock': 'אזל מהמלאי',
  'station_order.allDone': 'הכל מוכן',
  'station_order.closed': 'סגור',
  'station_order.closedHours': 'סגור בשעות אלו',
  'station_order.enjoy': 'בתאבון',
  'station_order.enterTable': 'הכנס מספר שולחן',
  'station_order.folioChargeHint': 'הוסף לחשבון החדר',
  'station_order.folioChargeNote': 'זה יחויב לחדר שלך',
  'station_order.folioChargeTitle': 'חיוב לחדר',
  'station_order.included': 'כלול',
  'station_order.maxOrdersReached': 'הגעת למספר ההזמנות המקסימלי',
  'station_order.maxOrdersReachedRoom': 'הגעת למספר ההזמנות המקסימלי לחדר',
  'station_order.nextShift': 'משמרת הבאה',
  'station_order.nextShiftNote': 'זמין במשמרת הבאה',
  'station_order.notes': 'הערות',
  'station_order.notesPlaceholder': 'בקשות מיוחדות...',
  'station_order.oops': 'אופס!',
  'station_order.orderFailed': 'ההזמנה נכשלה',
  'station_order.ordered': 'הוזמן',
  'station_order.placeAnother': 'בצע הזמנה נוספת',
  'station_order.placeOrder': 'בצע הזמנה',
  'station_order.editOrder': 'עריכת הזמנה',
  'station_order.cancelOrder': 'ביטול הזמנה',
  'station_order.confirmCancel': 'להקשה לאישור',
  'station_order.cancelFailed': 'ביטול ההזמנה נכשל',
  'station_order.updateOrder': 'עדכון הזמנה',
  'station_order.editingOrder': 'עריכת ההזמנה שלך',
  'station_order.editingNote': 'עדכנו את הפריטים ולחצו על עדכון הזמנה.',
  'station_order.preparing': 'בהכנה',
  'station_order.readyPickup': 'מוכן לאיסוף',
  'station_order.readyRoom': 'מוכן בחדר',
  'station_order.readyTable': 'מוכן בשולחן',
  'station_order.selectItems': 'בחר פריטים',
  'station_order.sending': 'שולח',
  'station_order.table': 'שולחן',
  'station_order.tableNumber': 'מספר שולחן',
  'station_order.thankYou': 'תודה',
  'station_order.unableLoad': 'לא ניתן לטעון',
  'station_order.yourOrder': 'ההזמנה שלך',
  'status_card.editOrder': 'ערוך הזמנה',
  'status_card.express': 'אקספרס',
  'status_card.expressSurcharge': 'תוספת אקספרס',
  'status_card.guest': 'אורח',
  'status_card.guests': 'אורחים',
  'status_card.hk.itemRequest': 'בקשת פריט',
  'status_card.hk.roomCleaning': 'ניקיון חדר',
  'status_card.hk.turndown': 'שירות סידור ערב',
  'status_card.issue': 'תקלה',
  'status_card.item': 'פריט',
  'status_card.partySize': 'מספר סועדים',
  'status_card.quotedPrice': 'מחיר שהוצע',
  'status_card.reason': 'סיבה',
  'status_card.request': 'בקשה',
  'status_card.scheduled': 'מתוזמן',
  'status_card.service': 'שירות',
  'status_card.station': 'עמדה',
  'status_card.step.acknowledged': 'התקבל',
  'status_card.step.cancelled': 'בוטל',
  'status_card.step.completed': 'הושלם',
  'status_card.step.confirmed': 'אושר',
  'status_card.step.declined': 'נדחה',
  'status_card.step.delivered': 'נמסר',
  'status_card.step.inProgress': 'בטיפול',
  'status_card.step.pending': 'ממתין',
  'status_card.step.preparing': 'בהכנה',
  'status_card.step.ready': 'מוכן',
  'status_card.step.received': 'התקבל',
  'status_card.step.reported': 'דווח',
  'status_card.step.resolved': 'נפתר',
  'status_card.step.sent': 'נשלח',
  'status_card.step.started': 'החל',
  'status_card.summary.laundry': 'כביסה',
  'status_card.summary.maintenance': 'תחזוקה',
  'status_card.summary.order': 'הזמנה',
  'status_card.summary.request': 'בקשה',
  'status_card.summary.reservation': 'הזמנת מקום',
  'status_card.summary.service': 'בקשת שירות',
  'status_card.svc.concierge': 'קונסיירז\'',
  'status_card.svc.informationRequest': 'בקשת מידע',
  'status_card.svc.luggage': 'סיוע במזוודות',
  'status_card.svc.other': 'אחר',
  'status_card.svc.roomService': 'שירות חדרים',
  'status_card.svc.specialRequest': 'בקשה מיוחדת',
  'status_card.svc.taxi': 'מונית / הסעה',
  'status_card.svc.transport': 'הסעות',
  'status_card.svc.wakeUpCall': 'שיחת השכמה',
  'status_card.type': 'סוג',
  'status_card.type.housekeeping': 'שירות חדרים',
  'status_card.type.laundry': 'כביסה',
  'status_card.type.maintenance': 'תחזוקה',
  'status_card.type.order': 'הזמנת אוכל',
  'status_card.type.reservation': 'הזמנת מקום',
  'status_card.type.service': 'בקשת שירות',
  'voucher.expires': 'פג בתאריך',
  'voucher.fixedValue': 'שווי {{amount}}',
  'voucher.freeItem': 'פריט חינם',
  'voucher.percentOff': '{{amount}}% הנחה',
  'voucher.redeemed': 'מומש',
  'voucher.statusActive': 'פעיל',
  'voucher.statusExpired': 'פג תוקף',
  'voucher.statusFullyUsed': 'נוצל במלואו',
  'voucher.usedCount': '{{used}} / {{max}} נוצלו',
  'vouchers.available': 'זמין',
  'vouchers.expires': 'פג בתאריך',
  'vouchers.noVouchers': 'אין שוברים',
  'vouchers.noVouchersDesc': 'אין שוברים זמינים',
  'vouchers.subtitle': 'השוברים שלי',
  'vouchers.title': 'השוברים שלי',
  'vouchers.used': 'נוצלו / פגי תוקף',
  'vouchers.uses': 'שימושים',
  'vouchers.validAt': 'תקף ב',
  'welcome.dayPass': 'כרטיס יומי',
  'welcome.event': 'אירוע',
  'welcome.lounge': 'טרקלין',
  'welcome.orderNow': 'הקש לצפייה בתפריט והזמנה',
  'welcome.ordering': 'הזמנה',
  'welcome.poolbar': 'בר בריכה',
  'welcome.services': 'שירותים',
  'welcome.sessionLabel': 'מושב',
  'welcome.spaSession': 'מושב',
  'welcome.table': 'שולחן',
  'welcome.verifyDesc': 'אמת את מספר הטלפון שלך כדי לפתוח חיובים לחשבון החדר ולאפשר מכשירים נוספים במושב.',
  'welcome.verifyNow': 'אמת',
  'welcome.verifyRoom': 'אמת את החדר שלך',

  // One session per stay: pre-arrival + post-stay modules
  'common.saving': 'שומר…',
  'common.continue': 'המשך',
  'common.backHome': 'חזרה לדף הבית',
  'common.done': 'סיום',
  'module.registration': 'רישום',
  'module.documents': 'מסמכים',
  'module.review': 'חוות דעת',
  'module.lost_property': 'אבידות',
  'welcome.registrationDesc': 'הפרטים והחתימה שלך לפני ההגעה',
  'welcome.documentsDesc': 'העלאת דרכון או תעודה מזהה',
  'welcome.beforeYouArrive': 'לפני ההגעה',
  'welcome.arrivingOn': 'הגעה',
  'welcome.registrationDone': 'הרישום הושלם. נתראה בקרוב!',
  'welcome.completeRegistration': 'השלמת הרישום',
  'welcome.reviewDesc': 'ספרו לנו איך הייתה השהייה',
  'welcome.lostPropertyDesc': 'שכחתם משהו?',
  'welcome.thanksForStaying': 'תודה שהתארחתם אצלנו',
  'welcome.postStayHint': 'החשבונית, ההודעות והאבידות זמינות כאן עוד כמה ימים.',
  'welcome.rateStay': 'דרגו את השהייה',
  'registration.title': 'רישום',
  'registration.subtitle': 'הפרטים שלך לשהייה',
  'registration.nameRequired': 'נא להזין שם פרטי ושם משפחה.',
  'registration.saveFailed': 'לא ניתן היה לשמור את הפרטים.',
  'registration.acceptTerms': 'יש לאשר את התנאים כדי להמשיך.',
  'registration.confirmFailed': 'לא ניתן היה להשלים את הרישום.',
  'registration.firstName': 'שם פרטי',
  'registration.lastName': 'שם משפחה',
  'registration.name': 'שם',
  'registration.email': 'אימייל',
  'registration.phone': 'טלפון',
  'registration.country': 'מדינה',
  'registration.passportId': 'דרכון / ת.ז.',
  'registration.dateOfBirth': 'תאריך לידה',
  'registration.party': 'אורחים',
  'registration.adults': 'מבוגרים',
  'registration.children': 'ילדים',
  'registration.babies': 'תינוקות',
  'registration.signature': 'חתימה (הקלידו שם מלא)',
  'registration.terms': 'אני מאשר/ת שהפרטים נכונים ומקבל/ת את תנאי המלון.',
  'registration.complete': 'השלמת הרישום',
  'registration.doneTitle': 'הכול מוכן',
  'registration.doneText': 'הרישום הושלם. נדאג שהכול יהיה מוכן להגעתכם.',
  'registration.addDocuments': 'העלאת מסמכים',
  'registration.edit': 'עריכת הפרטים',
  'documents.title': 'מסמכים',
  'documents.subtitle': 'דרכון או ת.ז. לכל הנרשמים',
  'documents.hint': 'JPG, PNG או PDF, עד 15MB.',
  'documents.passport': 'דרכון',
  'documents.idCard': 'תעודת זהות',
  'documents.visa': 'ויזה',
  'documents.other': 'אחר',
  'documents.add': 'הוספת קובץ',
  'documents.uploading': 'מעלה…',
  'documents.uploadFailed': 'ההעלאה נכשלה.',
  'documents.removeFailed': 'לא ניתן היה להסיר את המסמך.',
  'documents.none': 'עדיין לא הועלו מסמכים.',
  'documents.verified': 'מאומת',
  'documents.view': 'צפייה',
  'documents.locked': 'נעול',
  'documents.remove': 'הסרה',
  'review.title': 'דרגו את השהייה',
  'review.subtitle': 'תודה שהתארחתם אצלנו',
  'review.overall': 'כללי',
  'review.staff': 'צוות',
  'review.cleanliness': 'ניקיון',
  'review.comment': 'משהו שתרצו לספר לנו?',
  'review.send': 'שליחת חוות דעת',
  'review.update': 'עדכון חוות הדעת',
  'review.thanks': 'תודה! חוות הדעת נשלחה.',
  'review.empty': 'נא לתת דירוג או הערה.',
  'review.failed': 'לא ניתן היה לשלוח את חוות הדעת. נסו שוב.',
  'lostProperty.title': 'אבידות',
  'lostProperty.subtitle': 'שכחתם משהו? ספרו לנו',
  'lostProperty.item': 'מה שכחתם?',
  'lostProperty.description': 'תיאור (צבע, מותג…)',
  'lostProperty.where': 'איפה לדעתכם שכחתם?',
  'lostProperty.contact': 'הדרך הטובה ליצור איתכם קשר (לא חובה)',
  'lostProperty.send': 'שליחת דיווח',
  'lostProperty.itemRequired': 'נא לספר לנו מה שכחתם.',
  'lostProperty.failed': 'לא ניתן היה לשלוח את הדיווח. נסו שוב.',
  'lostProperty.sentTitle': 'קיבלנו את הדיווח',
  'lostProperty.sentText': 'נחפש ונענה לכם בהודעות.',
  'lostProperty.openMessages': 'פתיחת הודעות',
};

// Arabic translations
const ar: Translations = {
  // Common
  'common.loading': 'جاري التحميل...',
  'common.error': 'حدث خطأ',
  'common.submit': 'إرسال',
  'common.cancel': 'إلغاء',
  'common.confirm': 'تأكيد',
  'common.refresh': 'تحديث',
  'common.back': 'رجوع',
  'common.next': 'التالي',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.close': 'إغلاق',
  'common.save': 'حفظ',
  'common.tryAgain': 'حاول مرة أخرى',
  'common.sessionExpired': 'انتهت جلستك. الرجاء تسجيل الدخول مرة أخرى.',
  'common.networkError': 'مشكلة في الاتصال — تحقق من الإنترنت وحاول مرة أخرى.',
  'common.included': 'مشمول',
  'common.addToCart': 'إضافة',
  'common.removeFromCart': 'إزالة',
  // Breakfast
  'breakfast.title': 'قائمة الإفطار',
  'breakfast.subtitle': 'اختر وجبة الإفطار',
  'breakfast.orderPlaced': 'تم تقديم الطلب بنجاح!',
  'breakfast.orderFailed': 'فشل في تقديم الطلب',
  'breakfast.viewOrder': 'عرض الطلب',
  'breakfast.noItems': 'لا توجد عناصر متاحة',
  'breakfast.cart': 'السلة',
  'breakfast.checkout': 'الدفع',
  'breakfast.categoryEmpty': 'لا توجد عناصر في هذه الفئة',
  // Order
  'order.title': 'حالة الطلب',
  'order.order_number': 'رقم الطلب',
  'order.last_updated': 'آخر تحديث',
  'order.status.pending': 'قيد الانتظار',
  'order.status.confirmed': 'مؤكد',
  'order.status.preparing': 'قيد التحضير',
  'order.status.ready': 'جاهز',
  'order.status.delivered': 'تم التوصيل',
  'order.status.cancelled': 'ملغي',
  'order.notFound': 'الطلب غير موجود',
  'order.trackOrder': 'تتبع الطلب',
  // Breakfast Order
  'breakfast_order.title': 'طلب الإفطار',
  'breakfast_order.chooseItem': 'ماذا تريد؟',
  'breakfast_order.howMany': 'كم حصة؟',
  'breakfast_order.servings': 'حصص',
  'breakfast_order.notesTitle': 'طلبات خاصة؟',
  'breakfast_order.notesPlaceholder': 'مثال: بدون بصل، مقرمش أكثر...',
  'breakfast_order.placeOrder': 'تقديم الطلب',
  'breakfast_order.selectItem': 'اختر عنصراً للطلب',
  'breakfast_order.loading': 'جاري تحميل قائمة الإفطار...',
  'breakfast_order.oops': 'عذراً!',
  'breakfast_order.unableLoad': 'تعذر تحميل قائمة الإفطار',
  'breakfast_order.orderFailed': 'فشل في تقديم الطلب',
  'breakfast_order.kitchenClosed': 'المطبخ مغلق',
  'breakfast_order.closedHours': 'طلبات الإفطار متاحة من',
  'breakfast_order.seeTomorrow': 'نراكم صباح الغد!',
  'breakfast_order.allDone': 'انتهيت لهذا اليوم!',
  'breakfast_order.maxOrdersReached': 'وصلت للحد الأقصى من الطلبات اليوم',
  'breakfast_order.sending': 'جاري الإرسال للمطبخ...',
  'breakfast_order.preparing': 'إفطارك قيد التحضير!',
  'breakfast_order.yourOrder': 'طلبك',
  'breakfast_order.ordered': 'تم الطلب',
  'breakfast_order.readyPickup': 'طلبك جاهز! استلمه من محطة الإفطار.',
  'breakfast_order.enjoy': 'بالعافية!',
  'breakfast_order.table': 'طاولة',
  'breakfast_order.tableNumber': 'رقم الطاولة',
  'breakfast_order.enterTable': 'أدخل رقم الطاولة',
  'breakfast_order.whosOrdering': 'من يطلب؟',
  'breakfast_order.enterYourName': 'أدخل اسمك',
  'breakfast_order.existingGuests': 'اختر اسمك:',
  'breakfast_order.orNewGuest': 'أو أضف ضيفاً جديداً',
  'breakfast_order.slotsAvailable': 'أماكن متاحة',
  'breakfast_order.namesFull': 'جميع أماكن الضيوف في هذه الغرفة مشغولة.',
  'breakfast_order.changeName': 'تغيير',
  // Welcome
  'welcome.title': 'مرحباً',
  'welcome.roomNumber': 'الغرفة',
  'welcome.guestName': 'الضيف',
  'welcome.checkIn': 'تسجيل الوصول',
  'welcome.checkOut': 'تسجيل المغادرة',
  'welcome.modules': 'الخدمات المتاحة',
  'welcome.shareAccess': 'مشاركة الوصول',
  'welcome.shareDescription': 'شارك هذا الرابط مع ضيوف آخرين في غرفتك',
  'welcome.shareSuccess': 'تم نسخ الرابط!',
  'welcome.additionalDevice': 'إضافة جهاز آخر',
  'welcome.devicesUsed': 'الأجهزة',
  // Session
  'session.expired': 'انتهت صلاحية جلستك',
  'session.invalid': 'جلسة غير صالحة',
  'session.activating': 'جاري تفعيل جلستك...',
  // Errors
  'error.networkError': 'خطأ في الشبكة. يرجى التحقق من اتصالك.',
  'error.serverError': 'خطأ في الخادم. يرجى المحاولة لاحقاً.',
  'error.notFound': 'غير موجود',
  'error.unauthorized': 'وصول غير مصرح',
  // Module names
  'module.breakfast': 'قائمة الإفطار',
  'module.eggs': 'طلب الإفطار',
  'module.housekeeping': 'خدمة الغرف',
  'module.maintenance': 'الإبلاغ عن مشكلة',
  'module.roomservice': 'خدمة الغرف',
  'module.spa': 'سبا وعافية',
  'module.concierge': 'كونسيرج',
  'module.minibar': 'ميني بار',
  'module.guides': 'أدلة',
  // Navigation
  'nav.home': 'الرئيسية',
  'nav.more': 'المزيد',
  'nav.back': 'رجوع',
  'nav.allServices': 'جميع الخدمات',
  'nav.orderStatus': 'حالة الطلب',
  // Housekeeping
  'housekeeping.title': 'خدمة الغرف',
  'housekeeping.loading': 'جاري تحميل خدمات الغرف...',
  'housekeeping.unavailable': 'الخدمة غير متاحة',
  'housekeeping.unavailableMsg': 'طلبات خدمة الغرف غير متاحة حالياً.',
  'housekeeping.contactDesk': 'يرجى التواصل مع مكتب الاستقبال.',
  'housekeeping.limitReached': 'تم الوصول للحد اليومي',
  'housekeeping.tryTomorrow': 'يرجى المحاولة غداً!',
  'housekeeping.submitting': 'جاري إرسال الطلب...',
  'housekeeping.sendingMsg': 'نرسل طلبك لخدمة الغرف',
  'housekeeping.yourRequest': 'طلبك',
  'housekeeping.onTheWay': 'العناصر في طريقها لغرفتك!',
  'housekeeping.delivered': 'تم التوصيل! استمتع بإقامتك!',
  'housekeeping.estimatedDelivery': 'التوصيل المتوقع',
  'housekeeping.makeAnother': 'طلب آخر',
  'housekeeping.viewCart': 'عرض السلة',
  'housekeeping.specialRequests': 'طلبات خاصة (اختياري)',
  'housekeeping.specialPlaceholder': 'تعليمات خاصة...',
  'housekeeping.submitRequest': 'إرسال الطلب',
  'housekeeping.add': 'إضافة',
  'housekeeping.room': 'الغرفة',
  'housekeeping.items': 'عناصر',
  'housekeeping.item': 'عنصر',
  'housekeeping.minutes': 'دقائق',
  'housekeeping.requested': 'تم الطلب',
  // Maintenance
  'maintenance.title': 'الإبلاغ عن مشكلة',
  'maintenance.loading': 'جاري تحميل خدمات الصيانة...',
  'maintenance.unavailable': 'الخدمة غير متاحة',
  'maintenance.unavailableMsg': 'الإبلاغ عن مشاكل الصيانة غير متاح حالياً.',
  'maintenance.contactDesk': 'يرجى التواصل مع مكتب الاستقبال.',
  'maintenance.submitting': 'جاري إرسال البلاغ...',
  'maintenance.sendingMsg': 'نرسل بلاغك للصيانة',
  'maintenance.myReports': 'بلاغاتي',
  'maintenance.noReports': 'لم يتم تقديم بلاغات بعد',
  'maintenance.reportNew': 'إبلاغ عن مشكلة جديدة',
  'maintenance.reportAnother': 'إبلاغ عن مشكلة أخرى',
  'maintenance.viewAll': 'عرض جميع البلاغات',
  'maintenance.reportStatus': 'حالة البلاغ',
  'maintenance.resolved': 'تم الحل',
  'maintenance.reportIssue': 'الإبلاغ عن مشكلة',
  'maintenance.subject': 'الموضوع',
  'maintenance.subjectPlaceholder': 'وصف مختصر للمشكلة',
  'maintenance.details': 'التفاصيل (اختياري)',
  'maintenance.detailsPlaceholder': 'قدم مزيداً من التفاصيل حول المشكلة...',
  'maintenance.location': 'الموقع (اختياري)',
  'maintenance.locationPlaceholder': 'مثال: الحمام، بالقرب من النافذة',
  'maintenance.priority': 'الأولوية',
  'maintenance.priorityLow': 'منخفضة',
  'maintenance.priorityLowDesc': 'عند الإمكان',
  'maintenance.priorityNormal': 'عادية',
  'maintenance.priorityNormalDesc': 'استجابة عادية',
  'maintenance.priorityHigh': 'عالية',
  'maintenance.priorityHighDesc': 'يحتاج اهتمام سريع',
  'maintenance.priorityUrgent': 'عاجل',
  'maintenance.priorityUrgentDesc': 'يحتاج اهتمام فوري',
  'maintenance.submitReport': 'إرسال البلاغ',
  'maintenance.change': 'تغيير',
  'maintenance.customQuestion': 'لم تجد ما تبحث عنه؟',
  'maintenance.reportCustom': 'إبلاغ عن مشكلة مخصصة',
  'maintenance.reported': 'تم الإبلاغ',
  'maintenance.oops': 'عذراً!',
  // Welcome page
  'welcome.guests': 'الضيوف',
  'welcome.yourStay': 'إقامتك',
  'welcome.connectedDevices': 'الأجهزة المتصلة',
  'welcome.shareDeviceDesc': 'شارك الوصول مع ضيوف آخرين في غرفتك. يمكن لكل ضيف استخدام جهازه الخاص.',
  'welcome.guidesDesc': 'أدلة خطوة بخطوة ومعلومات',
  'welcome.addDevice': 'إضافة جهاز آخر',
  'welcome.processing': 'جاري المعالجة...',
  'welcome.availableServices': 'الخدمات المتاحة',
  'welcome.shareModalTitle': 'مشاركة الوصول',
  'welcome.shareModalDesc': 'شارك هذا الرابط مع ضيف آخر لمنحه الوصول على جهازه.',
  'welcome.shareLink': 'مشاركة الرابط',
  'welcome.copyLink': 'نسخ الرابط',
  'welcome.copied': 'تم النسخ!',
  'welcome.deviceSlots': 'فتحات الأجهزة',
  'welcome.sessionExpired': 'انتهت الجلسة',
  'welcome.scanAgain': 'يرجى مسح رمز QR مرة أخرى للوصول إلى الخدمات.',
  'welcome.welcome': 'مرحباً',
  // ---- auto-added: full guest-app coverage (June 2026) ----
  'activity.active': 'الطلبات النشطة',
  'activity.allPeople': 'الكل',
  'activity.completed': 'مكتمل',
  'activity.empty': 'لا يوجد نشاط بعد',
  'activity.emptySub': 'ستظهر طلباتك وحجوزاتك هنا',
  'activity.filter.all': 'الكل',
  'activity.filter.housekeeping': 'خدمة الغرف',
  'activity.filter.maintenance': 'الصيانة',
  'activity.filter.orders': 'الطلبات',
  'activity.filter.reservations': 'الحجوزات',
  'activity.filter.services': 'الخدمات',
  'activity.filter.vouchers': 'القسائم',
  'activity.title': 'نشاطي',
  'activity.vouchers': 'القسائم',
  'common.category': 'الفئة',
  'common.date': 'التاريخ',
  'common.hoursAgo': 'قبل {{n}} ساعة',
  'common.justNow': 'الآن',
  'common.minutesAgo': 'قبل {{n}} دقيقة',
  'common.ok': 'حسناً',
  'common.oops': 'عذراً!',
  'common.priority': 'الأولوية',
  'common.retry': 'إعادة المحاولة',
  'common.room': 'الغرفة',
  'common.total': 'المجموع',
  'folio.allCharges': 'عرض جميع رسوم الغرفة',
  'folio.balance': 'الرصيد المستحق',
  'folio.changeCard': 'تغيير البطاقة',
  'folio.charged': 'تم الخصم',
  'folio.charges': 'الرسوم',
  'folio.depositCaptured': 'تم تحصيل الوديعة',
  'folio.depositHeld': 'تم حجز الوديعة',
  'folio.depositReleased': 'تم تحرير الوديعة',
  'folio.downloadInvoice': 'تحميل الفاتورة',
  'folio.empty': 'لا توجد رسوم بعد',
  'folio.emptyHint': 'ستظهر هنا الرسوم من طلباتك وخدماتك.',
  'folio.hideHistory': 'إخفاء السجل',
  'folio.howToPay': 'كيفية الدفع',
  'folio.noCharges': 'لا توجد رسوم',
  'folio.paid': 'مدفوع',
  'folio.pay': 'ادفع',
  'folio.payAtCounter': 'الدفع عند المكتب',
  'folio.payAtCounterHint': 'توجه إلى مكتب الاستقبال',
  'folio.payHint': 'ادفع فاتورتك',
  'folio.payOnline': 'الدفع عبر الإنترنت',
  'folio.payOnlineHint': 'ادفع بأمان عبر الإنترنت',
  'folio.paymentSuccess': 'تم الدفع بنجاح',
  'folio.recentCharges': 'الرسوم الأخيرة',
  'folio.room': 'غرفة',
  'folio.roomCharges': 'رسوم الغرفة',
  'folio.saveCard': 'حفظ البطاقة',
  'folio.savedForPayments': 'محفوظة للمدفوعات المستقبلية',
  'folio.securePayment': 'دفع آمن',
  'folio.securityDeposit': 'وديعة التأمين',
  'folio.settleAll': 'تسوية الكل',
  'folio.settleSuccess': 'تمت تسوية الدفع بنجاح',
  'folio.settled': 'تمت التسوية',
  'folio.payViaHotel': 'تُضاف الرسوم إلى فاتورة الفندق. يتم الدفع عند المغادرة بالبطاقة المسجلة لدى الفندق أو في مكتب الاستقبال.',
  'folio.statusDue': 'المبلغ مستحق — يرجى التسوية في مكتب الاستقبال',
  'folio.paidAtHotel': 'تم الدفع في الفندق',
  'folio.cardRequiredHint': 'احفظ بطاقة دفع لطلب العناصر المدفوعة — يتم الخصم منها عند الطلب.',
  'folio.chargedAtPurchase': 'يتم خصم العناصر المدفوعة من بطاقتك المحفوظة عند الطلب.',
  'folio.pmsPosted': 'مضاف إلى فاتورة الفندق',
  'folio.pmsPending': 'تتم إضافته إلى فاتورة الفندق',
  'folio.pmsBeingChecked': 'قيد المراجعة من الفندق',
  'folio.pmsFrontDesk': 'تمت التسوية في الاستقبال',
  'station_order.saveCard': 'حفظ بطاقة',
  'folio.showHistory': 'عرض سجل النشاط',
  'folio.title': 'حسابي',
  'folio.total': 'المجموع',
  'folio.waitingForTap': 'في انتظار اللمس',
  'folio.waitingForTapDesc': 'قرّب بطاقتك من القارئ',
  'footer.dataDeletion': 'حذف البيانات',
  'footer.privacy': 'سياسة الخصوصية',
  'footer.terms': 'شروط الخدمة',
  'guides.finish': 'إنهاء',
  'guides.guides': 'الأدلة',
  'guides.iUnderstand': 'فهمت',
  'guides.next': 'التالي',
  'guides.nextIn': 'التالي خلال',
  'guides.noCategories': 'لا توجد فئات',
  'guides.noGuides': 'لا توجد أدلة',
  'guides.previous': 'السابق',
  'guides.steps': 'الخطوات',
  'guides.subtitle': 'الأدلة',
  'guides.title': 'الأدلة',
  'laundry.acknowledgedMsg': 'تم الاستلام',
  'laundry.activeOrder': 'الطلب النشط',
  'laundry.add': 'إضافة',
  'laundry.contactDesk': 'يرجى التواصل مع مكتب الاستقبال',
  'laundry.deliveredMsg': 'تم التوصيل',
  'laundry.each': 'للقطعة',
  'laundry.estimatedProcessing': 'المعالجة المتوقعة',
  'laundry.estimatedReady': 'الجاهزية المتوقعة',
  'laundry.expressDelivery': 'توصيل سريع',
  'laundry.expressFee': 'رسوم الخدمة السريعة',
  'laundry.expressMsg': 'خدمة سريعة',
  'laundry.history': 'السجل',
  'laundry.hours': 'ساعات',
  'laundry.item': 'قطعة',
  'laundry.items': 'قطع',
  'laundry.limitReached': 'تم الوصول للحد الأقصى',
  'laundry.newRequest': 'طلب جديد',
  'laundry.noOrders': 'لا توجد طلبات',
  'laundry.notesPlaceholder': 'تعليمات خاصة...',
  'laundry.receivedMsg': 'تم الاستلام',
  'laundry.room': 'الغرفة',
  'laundry.sendingMsg': 'جاري إرسال الطلب...',
  'laundry.sentMsg': 'تم الإرسال',
  'laundry.specialInstructions': 'تعليمات خاصة',
  'laundry.submitOrder': 'إرسال الطلب',
  'laundry.editOrder': 'تعديل الطلب',
  'laundry.cancelOrder': 'إلغاء الطلب',
  'laundry.confirmCancel': 'اضغط للتأكيد',
  'laundry.cancelFailed': 'فشل إلغاء الطلب',
  'laundry.updateOrder': 'تحديث الطلب',
  'laundry.editingOrder': 'تعديل طلبك',
  'laundry.editingNote': 'عدّل العناصر ثم اضغط على تحديث.',
  'laundry.submitted': 'تم الإرسال',
  'laundry.submitting': 'جاري الإرسال...',
  'laundry.subtotal': 'المجموع الفرعي',
  'laundry.title': 'الغسيل',
  'laundry.total': 'المجموع',
  'laundry.tryTomorrow': 'حاول غداً',
  'laundry.unavailable': 'غير متاح',
  'laundry.unavailableMsg': 'الخدمة غير متاحة',
  'laundry.viewBag': 'عرض الحقيبة',
  'laundry.viewHistory': 'عرض السجل',
  'laundry.yourBag': 'حقيبتك',
  'laundry.yourOrder': 'طلبك',
  'maintenance.descriptionTooShort': 'يجب أن يكون الوصف 3 أحرف على الأقل',
  'maintenance.room': 'الغرفة',
  'maintenance.subjectRequired': 'يرجى إدخال موضوع للبلاغ',
  'messages.empty': 'لا توجد رسائل',
  'messages.namePrompt.description': 'سيُستخدم هذا للتعرف على طلباتك وحجوزاتك',
  'messages.namePrompt.placeholder': 'أدخل اسمك',
  'messages.namePrompt.submit': 'إرسال',
  'messages.namePrompt.title': 'ما اسمك؟',
  'messages.placeholder': 'اكتب رسالة...',
  'messages.subtitle': 'الرسائل',
  'messages.title': 'الرسائل',
  'messages.whatsapp.changeNumber': 'تغيير الرقم',
  'messages.whatsapp.checkWhatsApp': 'تحقق من WhatsApp',
  'messages.whatsapp.codeSentViaSms': 'تم إرسال الرمز عبر SMS',
  'messages.whatsapp.codeSentViaWhatsApp': 'تم إرسال الرمز عبر WhatsApp',
  'messages.whatsapp.connectDescription': 'اربط WhatsApp الخاص بك لتلقي الرسائل',
  'messages.whatsapp.enterCode': 'أدخل الرمز',
  'messages.whatsapp.modalTitle': 'التحقق من WhatsApp',
  'messages.whatsapp.phonePlaceholder': 'أدخل رقم هاتفك',
  'messages.whatsapp.phoneRequired': 'رقم الهاتف مطلوب',
  'messages.whatsapp.sendVerificationCode': 'إرسال رمز التحقق',
  'messages.whatsapp.sendViaSms': 'إرسال عبر SMS',
  'messages.whatsapp.skipForNow': 'تخطي الآن',
  'messages.whatsapp.useSmsInstead': 'استخدم SMS بدلاً من ذلك',
  'messages.whatsapp.useWhatsAppInstead': 'استخدم WhatsApp بدلاً من ذلك',
  'messages.whatsapp.verified': 'تم التحقق',
  'messages.whatsapp.verifiedDescription': 'تم التحقق من WhatsApp',
  'messages.whatsapp.verify': 'تحقق',
  'messages.whatsapp.verifyButton': 'تحقق',
  'namePrompt.continue': 'متابعة',
  'namePrompt.description': 'سيُستخدم هذا للتعرف على طلباتك',
  'namePrompt.placeholder': 'أدخل اسمك',
  'namePrompt.title': 'ما اسمك؟',
  'namePrompt.useBookingName': 'استخدم اسم الحجز:',
  'nav.account': 'الحساب',
  'documents.mine': 'أنا (غرفتي)',
  'documents.forWhom': 'لمن هذا المستند؟',
  'documents.moveTo': 'نقل إلى…',
  'documents.moveFailed': 'تعذّر نقل المستند.',
  'profile.bookedForOthers': 'حجزتها أنت',
  'rooms.shareUnavailable': 'الوصول إلى هذه الغرفة غير متاح حاليًا.',
  'rooms.myTitle': 'غرفي',
  'rooms.myIntro': 'هذه الغرف ضمن حجزك وتستخدم هاتفك أو بريدك. شارك الوصول لكل غرفة مع ضيوفها، أو سجّل للغرفة بيانات اتصال خاصة ليحصل ضيفها على وصوله ونقاطه الخاصة.',
  'rooms.noneLeft': 'لكل غرفة في حجزك الآن ضيفها الخاص.',
  'rooms.otherBooking': 'هذه الغرفة ضمن حجز آخر ببيانات اتصالك — سجّل لها بيانات اتصال خاصة.',
  'rooms.shareHint': 'دع ضيوف الغرفة يمسحون الرمز، أو أرسل لهم الرابط لفتحه على هواتفهم.',
  'rooms.copied': 'تم نسخ الرابط',
  'rooms.sendLink': 'إرسال الرابط',
  'rooms.share': 'مشاركة الوصول',
  'rooms.shareAfterCheckIn': 'يمكنك مشاركة الوصول بعد تسجيل دخول الغرفة.',
  'rooms.ownContact': 'بيانات اتصال خاصة للغرفة',
  'rooms.bill': 'الفاتورة',
  'rooms.shareText': 'وصولك إلى الغرفة {{room}}',
  'rooms.cardTitleManaged': 'غرفك',
  'rooms.cardBodyManaged': '{{n}} غرفة أخرى في حجزك. شارك الوصول مع ضيوفها، أو سجّل لكل غرفة بيانات اتصال خاصة ليكسبوا نقاطهم.',
  'rooms.cardButtonManaged': 'غرفي',
  'profile.managedRoom': 'هذه الغرفة جزء من حجز {{booker}}. لتكسب نقاطًا عن إقامتك، اطلب من {{booker}} أو من الاستقبال تسجيل رقم هاتفك لهذه الغرفة.',
  'rooms.title': 'سجّل غرفك',
  'rooms.cardTitle': 'غرفك الأخرى',
  'rooms.cardBody': '{{n}} غرفة أخرى في هذا الحجز تستخدم هاتفك أو بريدك. أخبرنا من يقيم في كل غرفة ليحصل كل منها على وصول خاص.',
  'rooms.cardButton': 'سجّل غرفك',
  'rooms.room': 'غرفة',
  'rooms.loadError': 'تعذّر تحميل غرفك.',
  'rooms.saveError': 'تعذّر الحفظ. تحقق من التفاصيل.',
  'rooms.doneTitle': 'شكرًا!',
  'rooms.doneBody': 'لكل غرفة الآن ضيفها الخاص، وسيحصل كل منهم على وصول خاص للإقامة.',
  'rooms.none': 'لا توجد غرف للتسجيل.',
  'rooms.verifyFirst': 'تحقق من رقم هاتفك على هذا الجهاز لتسجيل غرفك.',
  'rooms.intro': 'هذه الغرف تستخدم هاتفك أو بريدك. أدخل من يقيم في كل منها وهاتفه أو بريده الخاص ليحصل كل منها على وصول خاص.',
  'rooms.guestName': 'اسم الضيف',
  'rooms.noContact': 'بدون هاتف أو بريد (مثلاً طفل) — سأتولى هذه الغرفة',
  'rooms.save': 'حفظ',
  'profile.title': 'ملفي الشخصي',
  'profile.loadError': 'تعذّر تحميل ملفك الشخصي.',
  'profile.joinError': 'تعذّر الانضمام. حاول مرة أخرى.',
  'profile.joinTitle': 'انضم إلى مكافآت {{chain}}',
  'profile.joinBody': 'اكسب {{points}} نقطة عن كل ليلة تقضيها معنا، في أي من فنادقنا. تُحفظ إقاماتك ونقاطك في ملفك الشخصي.',
  'profile.unavailable': 'المكافآت غير متاحة في هذا الفندق.',
  'profile.verifyFirst': 'تحقق من رقم هاتفك على هذا الجهاز للانضمام.',
  'profile.phone': 'الهاتف',
  'profile.email': 'البريد الإلكتروني',
  'profile.marketing': 'أرسلوا لي العروض والأخبار (اختياري)',
  'profile.consent': 'بالانضمام توافق على أن نحتفظ بملفك وإقاماتك ونقاطك إلى أن تطلب حذفها.',
  'profile.join': 'انضمام',
  'profile.notNow': 'ليس الآن',
  'profile.points': 'نقطة',
  'profile.memberSince': 'عضو منذ {{date}}',
  'profile.details': 'بياناتي',
  'profile.name': 'الاسم',
  'profile.stays': 'إقاماتي',
  'profile.noStays': 'لا توجد إقامات بعد.',
  'profile.nights': '{{n}} ليالٍ',
  'profile.history': 'سجل النقاط',
  'profile.noPoints': 'ستظهر نقاطك هنا بعد كل إقامة.',
  'profile.earned': 'إقامة',
  'profile.adjusted': 'تعديل',
  'profile.onReservationOf': 'ضمن حجز {{name}}',
  'nav.activity': 'نشاطي',
  'nav.guides': 'الأدلة',
  'nav.housekeeping': 'خدمة الغرف',
  'nav.maintenance': 'الصيانة',
  'nav.menu': 'القائمة',
  'nav.messages': 'الرسائل',
  'nav.notifications': 'الإشعارات',
  'nav.vouchers': 'القسائم',
  'payment.backToFolio': 'العودة إلى الحساب',
  'payment.cancelled': 'تم إلغاء الدفع',
  'payment.cancelledMessage': 'تم إلغاء دفعتك',
  'payment.cardSavedAuto': 'تم حفظ البطاقة تلقائياً',
  'payment.failed': 'فشل الدفع',
  'payment.failedMessage': 'فشلت دفعتك',
  'payment.success': 'تم الدفع بنجاح',
  'payment.successMessage': 'تمت دفعتك بنجاح',
  'payment.tryAgain': 'حاول مرة أخرى',
  'station_order.add': 'إضافة',
  'station_order.outOfStock': 'نفد من المخزون',
  'station_order.allDone': 'اكتمل كل شيء',
  'station_order.closed': 'مغلق',
  'station_order.closedHours': 'مغلق خلال هذه الساعات',
  'station_order.enjoy': 'بالعافية',
  'station_order.enterTable': 'أدخل رقم الطاولة',
  'station_order.folioChargeHint': 'أضف إلى حساب الغرفة',
  'station_order.folioChargeNote': 'سيتم خصم هذا من حساب غرفتك',
  'station_order.folioChargeTitle': 'الخصم من الغرفة',
  'station_order.included': 'مشمول',
  'station_order.maxOrdersReached': 'تم الوصول للحد الأقصى من الطلبات',
  'station_order.maxOrdersReachedRoom': 'تم الوصول للحد الأقصى من الطلبات للغرفة',
  'station_order.nextShift': 'الفترة التالية',
  'station_order.nextShiftNote': 'متاح في الفترة التالية',
  'station_order.notes': 'ملاحظات',
  'station_order.notesPlaceholder': 'طلبات خاصة...',
  'station_order.oops': 'عذراً!',
  'station_order.orderFailed': 'فشل الطلب',
  'station_order.ordered': 'تم الطلب',
  'station_order.placeAnother': 'تقديم طلب آخر',
  'station_order.placeOrder': 'تقديم الطلب',
  'station_order.editOrder': 'تعديل الطلب',
  'station_order.cancelOrder': 'إلغاء الطلب',
  'station_order.confirmCancel': 'اضغط للتأكيد',
  'station_order.cancelFailed': 'فشل إلغاء الطلب',
  'station_order.updateOrder': 'تحديث الطلب',
  'station_order.editingOrder': 'تعديل طلبك',
  'station_order.editingNote': 'عدّل العناصر ثم اضغط على تحديث الطلب.',
  'station_order.preparing': 'قيد التحضير',
  'station_order.readyPickup': 'جاهز للاستلام',
  'station_order.readyRoom': 'جاهز في الغرفة',
  'station_order.readyTable': 'جاهز على الطاولة',
  'station_order.selectItems': 'اختر العناصر',
  'station_order.sending': 'جاري الإرسال',
  'station_order.table': 'الطاولة',
  'station_order.tableNumber': 'رقم الطاولة',
  'station_order.thankYou': 'شكراً لك',
  'station_order.unableLoad': 'تعذر التحميل',
  'station_order.yourOrder': 'طلبك',
  'status_card.editOrder': 'تعديل الطلب',
  'status_card.express': 'سريع',
  'status_card.expressSurcharge': 'رسوم الخدمة السريعة',
  'status_card.guest': 'ضيف',
  'status_card.guests': 'ضيوف',
  'status_card.hk.itemRequest': 'طلب عنصر',
  'status_card.hk.roomCleaning': 'تنظيف الغرفة',
  'status_card.hk.turndown': 'خدمة التهيئة المسائية',
  'status_card.issue': 'المشكلة',
  'status_card.item': 'العنصر',
  'status_card.partySize': 'عدد الأشخاص',
  'status_card.quotedPrice': 'السعر المعروض',
  'status_card.reason': 'السبب',
  'status_card.request': 'الطلب',
  'status_card.scheduled': 'مجدول',
  'status_card.service': 'الخدمة',
  'status_card.station': 'المحطة',
  'status_card.step.acknowledged': 'تم الاستلام',
  'status_card.step.cancelled': 'ملغي',
  'status_card.step.completed': 'مكتمل',
  'status_card.step.confirmed': 'مؤكد',
  'status_card.step.declined': 'مرفوض',
  'status_card.step.delivered': 'تم التوصيل',
  'status_card.step.inProgress': 'قيد التنفيذ',
  'status_card.step.pending': 'قيد الانتظار',
  'status_card.step.preparing': 'قيد التحضير',
  'status_card.step.ready': 'جاهز',
  'status_card.step.received': 'تم الاستلام',
  'status_card.step.reported': 'تم الإبلاغ',
  'status_card.step.resolved': 'تم الحل',
  'status_card.step.sent': 'تم الإرسال',
  'status_card.step.started': 'بدأ',
  'status_card.summary.laundry': 'الغسيل',
  'status_card.summary.maintenance': 'الصيانة',
  'status_card.summary.order': 'طلب',
  'status_card.summary.request': 'طلب',
  'status_card.summary.reservation': 'حجز',
  'status_card.summary.service': 'طلب خدمة',
  'status_card.svc.concierge': 'كونسيرج',
  'status_card.svc.informationRequest': 'طلب معلومات',
  'status_card.svc.luggage': 'مساعدة في الأمتعة',
  'status_card.svc.other': 'أخرى',
  'status_card.svc.roomService': 'خدمة الغرف',
  'status_card.svc.specialRequest': 'طلب خاص',
  'status_card.svc.taxi': 'سيارة أجرة / توصيل',
  'status_card.svc.transport': 'النقل',
  'status_card.svc.wakeUpCall': 'مكالمة إيقاظ',
  'status_card.type': 'النوع',
  'status_card.type.housekeeping': 'خدمة الغرف',
  'status_card.type.laundry': 'الغسيل',
  'status_card.type.maintenance': 'الصيانة',
  'status_card.type.order': 'طلب طعام',
  'status_card.type.reservation': 'حجز',
  'status_card.type.service': 'طلب خدمة',
  'voucher.expires': 'تنتهي في',
  'voucher.fixedValue': 'قيمة {{amount}}',
  'voucher.freeItem': 'عنصر مجاني',
  'voucher.percentOff': 'خصم {{amount}}%',
  'voucher.redeemed': 'تم الاستبدال',
  'voucher.statusActive': 'نشطة',
  'voucher.statusExpired': 'منتهية',
  'voucher.statusFullyUsed': 'مستخدمة بالكامل',
  'voucher.usedCount': '{{used}} / {{max}} مستخدمة',
  'vouchers.available': 'متاحة',
  'vouchers.expires': 'تنتهي في',
  'vouchers.noVouchers': 'لا توجد قسائم',
  'vouchers.noVouchersDesc': 'لا توجد قسائم متاحة',
  'vouchers.subtitle': 'قسائمي',
  'vouchers.title': 'قسائمي',
  'vouchers.used': 'مستخدمة / منتهية',
  'vouchers.uses': 'الاستخدامات',
  'vouchers.validAt': 'صالحة في',
  'welcome.dayPass': 'تذكرة يومية',
  'welcome.event': 'فعالية',
  'welcome.lounge': 'الصالة',
  'welcome.orderNow': 'اضغط لعرض القائمة والطلب',
  'welcome.ordering': 'الطلب',
  'welcome.poolbar': 'بار المسبح',
  'welcome.services': 'الخدمات',
  'welcome.sessionLabel': 'الجلسة',
  'welcome.spaSession': 'الجلسة',
  'welcome.table': 'الطاولة',
  'welcome.verifyDesc': 'وثّق هاتفك لفتح إمكانية الخصم من الحساب والسماح لأجهزة إضافية بالانضمام إلى الجلسة.',
  'welcome.verifyNow': 'تحقق',
  'welcome.verifyRoom': 'تحقق من غرفتك',

  // One session per stay: pre-arrival + post-stay modules
  'common.saving': 'جارٍ الحفظ…',
  'common.continue': 'متابعة',
  'common.backHome': 'العودة إلى الصفحة الرئيسية',
  'common.done': 'تم',
  'module.registration': 'التسجيل',
  'module.documents': 'المستندات',
  'module.review': 'التقييم',
  'module.lost_property': 'المفقودات',
  'welcome.registrationDesc': 'بياناتك وتوقيعك قبل الوصول',
  'welcome.documentsDesc': 'ارفع جواز سفرك أو بطاقة هويتك',
  'welcome.beforeYouArrive': 'قبل وصولك',
  'welcome.arrivingOn': 'الوصول',
  'welcome.registrationDone': 'اكتمل تسجيلك. نراك قريباً!',
  'welcome.completeRegistration': 'أكمل تسجيلك',
  'welcome.reviewDesc': 'أخبرنا عن إقامتك',
  'welcome.lostPropertyDesc': 'هل نسيت شيئاً؟',
  'welcome.thanksForStaying': 'شكراً لإقامتك معنا',
  'welcome.postStayHint': 'فاتورتك ورسائلك والمفقودات متاحة هنا لبضعة أيام أخرى.',
  'welcome.rateStay': 'قيّم إقامتك',
  'registration.title': 'التسجيل',
  'registration.subtitle': 'بياناتك للإقامة',
  'registration.nameRequired': 'يرجى إدخال الاسم الأول واسم العائلة.',
  'registration.saveFailed': 'تعذّر حفظ بياناتك.',
  'registration.acceptTerms': 'يرجى الموافقة على الشروط للمتابعة.',
  'registration.confirmFailed': 'تعذّر إكمال تسجيلك.',
  'registration.firstName': 'الاسم الأول',
  'registration.lastName': 'اسم العائلة',
  'registration.name': 'الاسم',
  'registration.email': 'البريد الإلكتروني',
  'registration.phone': 'الهاتف',
  'registration.country': 'الدولة',
  'registration.passportId': 'جواز السفر / الهوية',
  'registration.dateOfBirth': 'تاريخ الميلاد',
  'registration.party': 'الضيوف',
  'registration.adults': 'البالغون',
  'registration.children': 'الأطفال',
  'registration.babies': 'الرضّع',
  'registration.signature': 'التوقيع (اكتب اسمك الكامل)',
  'registration.terms': 'أؤكد أن البيانات أعلاه صحيحة وأوافق على شروط وأحكام المنشأة.',
  'registration.complete': 'إكمال التسجيل',
  'registration.doneTitle': 'كل شيء جاهز',
  'registration.doneText': 'اكتمل تسجيلك. سنجهّز كل شيء لوصولك.',
  'registration.addDocuments': 'رفع المستندات',
  'registration.edit': 'تعديل بياناتي',
  'documents.title': 'المستندات',
  'documents.subtitle': 'جواز سفر أو هوية لكل المسجّلين',
  'documents.hint': 'JPG أو PNG أو PDF، حتى 15 ميغابايت.',
  'documents.passport': 'جواز السفر',
  'documents.idCard': 'بطاقة الهوية',
  'documents.visa': 'التأشيرة',
  'documents.other': 'أخرى',
  'documents.add': 'إضافة ملف',
  'documents.uploading': 'جارٍ الرفع…',
  'documents.uploadFailed': 'فشل الرفع.',
  'documents.removeFailed': 'تعذّر حذف المستند.',
  'documents.none': 'لم يتم رفع أي مستندات بعد.',
  'documents.verified': 'موثّق',
  'documents.view': 'عرض',
  'documents.locked': 'مقفل',
  'documents.remove': 'حذف',
  'review.title': 'قيّم إقامتك',
  'review.subtitle': 'شكراً لإقامتك معنا',
  'review.overall': 'بشكل عام',
  'review.staff': 'الموظفون',
  'review.cleanliness': 'النظافة',
  'review.comment': 'هل هناك ما تود إخبارنا به؟',
  'review.send': 'إرسال التقييم',
  'review.update': 'تحديث التقييم',
  'review.thanks': 'شكراً! تم إرسال تقييمك.',
  'review.empty': 'يرجى إعطاء تقييم أو تعليق.',
  'review.failed': 'تعذّر إرسال تقييمك. حاول مرة أخرى.',
  'lostProperty.title': 'المفقودات',
  'lostProperty.subtitle': 'نسيت شيئاً؟ أخبرنا',
  'lostProperty.item': 'ماذا نسيت؟',
  'lostProperty.description': 'الوصف (اللون، العلامة التجارية…)',
  'lostProperty.where': 'أين تعتقد أنك نسيته؟',
  'lostProperty.contact': 'أفضل طريقة للتواصل معك (اختياري)',
  'lostProperty.send': 'إرسال البلاغ',
  'lostProperty.itemRequired': 'يرجى إخبارنا بما نسيته.',
  'lostProperty.failed': 'تعذّر إرسال البلاغ. حاول مرة أخرى.',
  'lostProperty.sentTitle': 'تلقّينا بلاغك',
  'lostProperty.sentText': 'سنبحث عنه ونرد عليك في الرسائل.',
  'lostProperty.openMessages': 'فتح الرسائل',
};

// Russian translations
const ru: Translations = {
  // Common
  'common.loading': 'Загрузка...',
  'common.error': 'Произошла ошибка',
  'common.submit': 'Отправить',
  'common.cancel': 'Отмена',
  'common.confirm': 'Подтвердить',
  'common.refresh': 'Обновить',
  'common.back': 'Назад',
  'common.next': 'Далее',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.close': 'Закрыть',
  'common.save': 'Сохранить',
  'common.tryAgain': 'Попробовать снова',
  'common.sessionExpired': 'Срок сессии истёк. Пожалуйста, войдите снова.',
  'common.networkError': 'Проблема с соединением — проверьте интернет и попробуйте снова.',
  'common.included': 'Включено',
  'common.addToCart': 'Добавить',
  'common.removeFromCart': 'Удалить',
  // Breakfast
  'breakfast.title': 'Меню завтрака',
  'breakfast.subtitle': 'Выберите блюда для завтрака',
  'breakfast.orderPlaced': 'Заказ успешно оформлен!',
  'breakfast.orderFailed': 'Не удалось оформить заказ',
  'breakfast.viewOrder': 'Посмотреть заказ',
  'breakfast.noItems': 'Нет доступных позиций',
  'breakfast.cart': 'Корзина',
  'breakfast.checkout': 'Оформить',
  'breakfast.categoryEmpty': 'В этой категории нет позиций',
  // Order
  'order.title': 'Статус заказа',
  'order.order_number': 'Номер заказа',
  'order.last_updated': 'Последнее обновление',
  'order.status.pending': 'Ожидает',
  'order.status.confirmed': 'Подтверждён',
  'order.status.preparing': 'Готовится',
  'order.status.ready': 'Готов',
  'order.status.delivered': 'Доставлен',
  'order.status.cancelled': 'Отменён',
  'order.notFound': 'Заказ не найден',
  'order.trackOrder': 'Отследить заказ',
  // Breakfast Order
  'breakfast_order.title': 'Заказ завтрака',
  'breakfast_order.chooseItem': 'Что вы желаете?',
  'breakfast_order.howMany': 'Сколько порций?',
  'breakfast_order.servings': 'порций',
  'breakfast_order.notesTitle': 'Особые пожелания?',
  'breakfast_order.notesPlaceholder': 'Например: Без лука, пожаристее...',
  'breakfast_order.placeOrder': 'Оформить заказ',
  'breakfast_order.selectItem': 'Выберите блюдо',
  'breakfast_order.loading': 'Загружаем меню завтрака...',
  'breakfast_order.oops': 'Упс!',
  'breakfast_order.unableLoad': 'Не удалось загрузить меню завтрака',
  'breakfast_order.orderFailed': 'Не удалось оформить заказ',
  'breakfast_order.kitchenClosed': 'Кухня закрыта',
  'breakfast_order.closedHours': 'Заказы завтрака доступны с',
  'breakfast_order.seeTomorrow': 'Увидимся завтра утром!',
  'breakfast_order.allDone': 'На сегодня всё!',
  'breakfast_order.maxOrdersReached': 'Достигнут лимит заказов на сегодня',
  'breakfast_order.sending': 'Отправляем на кухню...',
  'breakfast_order.preparing': 'Ваш завтрак готовится!',
  'breakfast_order.yourOrder': 'Ваш заказ',
  'breakfast_order.ordered': 'Заказано',
  'breakfast_order.readyPickup': 'Ваш заказ готов! Заберите на станции завтрака.',
  'breakfast_order.enjoy': 'Приятного аппетита!',
  'breakfast_order.table': 'Стол',
  'breakfast_order.tableNumber': 'Номер стола',
  'breakfast_order.enterTable': 'Введите номер стола',
  'breakfast_order.whosOrdering': 'Кто заказывает?',
  'breakfast_order.enterYourName': 'Введите ваше имя',
  'breakfast_order.existingGuests': 'Выберите ваше имя:',
  'breakfast_order.orNewGuest': 'или добавьте нового гостя',
  'breakfast_order.slotsAvailable': 'мест доступно',
  'breakfast_order.namesFull': 'Все места для гостей в этом номере заняты.',
  'breakfast_order.changeName': 'Изменить',
  // Welcome
  'welcome.title': 'Добро пожаловать',
  'welcome.roomNumber': 'Номер',
  'welcome.guestName': 'Гость',
  'welcome.checkIn': 'Заезд',
  'welcome.checkOut': 'Выезд',
  'welcome.modules': 'Доступные услуги',
  'welcome.shareAccess': 'Поделиться доступом',
  'welcome.shareDescription': 'Поделитесь ссылкой с другими гостями в номере',
  'welcome.shareSuccess': 'Ссылка скопирована!',
  'welcome.additionalDevice': 'Добавить устройство',
  'welcome.devicesUsed': 'Устройств',
  // Session
  'session.expired': 'Ваша сессия истекла',
  'session.invalid': 'Недействительная сессия',
  'session.activating': 'Активация сессии...',
  // Errors
  'error.networkError': 'Ошибка сети. Проверьте подключение.',
  'error.serverError': 'Ошибка сервера. Попробуйте позже.',
  'error.notFound': 'Не найдено',
  'error.unauthorized': 'Нет доступа',
  // Module names
  'module.breakfast': 'Меню завтрака',
  'module.eggs': 'Заказ завтрака',
  'module.housekeeping': 'Обслуживание номера',
  'module.maintenance': 'Сообщить о проблеме',
  'module.roomservice': 'Обслуживание номера',
  'module.spa': 'Спа и оздоровление',
  'module.concierge': 'Консьерж',
  'module.minibar': 'Мини-бар',
  'module.guides': 'Гиды',
  // Navigation
  'nav.home': 'Главная',
  'nav.more': 'Ещё',
  'nav.back': 'Назад',
  'nav.allServices': 'Все услуги',
  'nav.orderStatus': 'Статус заказа',
  // Housekeeping
  'housekeeping.title': 'Обслуживание номера',
  'housekeeping.loading': 'Загрузка услуг обслуживания...',
  'housekeeping.unavailable': 'Услуга недоступна',
  'housekeeping.unavailableMsg': 'Запросы на обслуживание номера сейчас недоступны.',
  'housekeeping.contactDesk': 'Обратитесь на стойку регистрации.',
  'housekeeping.limitReached': 'Дневной лимит достигнут',
  'housekeeping.tryTomorrow': 'Попробуйте завтра!',
  'housekeeping.submitting': 'Отправка запроса...',
  'housekeeping.sendingMsg': 'Отправляем ваш запрос в службу обслуживания',
  'housekeeping.yourRequest': 'Ваш запрос',
  'housekeeping.onTheWay': 'Предметы уже в пути к вашему номеру!',
  'housekeeping.delivered': 'Доставлено! Приятного отдыха!',
  'housekeeping.estimatedDelivery': 'Примерная доставка',
  'housekeeping.makeAnother': 'Ещё один запрос',
  'housekeeping.viewCart': 'Посмотреть корзину',
  'housekeeping.specialRequests': 'Особые пожелания (необязательно)',
  'housekeeping.specialPlaceholder': 'Особые инструкции...',
  'housekeeping.submitRequest': 'Отправить запрос',
  'housekeeping.add': 'Добавить',
  'housekeeping.room': 'Номер',
  'housekeeping.items': 'предметов',
  'housekeeping.item': 'предмет',
  'housekeeping.minutes': 'минут',
  'housekeeping.requested': 'Запрошено',
  // Maintenance
  'maintenance.title': 'Сообщить о проблеме',
  'maintenance.loading': 'Загрузка служб обслуживания...',
  'maintenance.unavailable': 'Услуга недоступна',
  'maintenance.unavailableMsg': 'Сообщения о проблемах сейчас недоступны.',
  'maintenance.contactDesk': 'Обратитесь на стойку регистрации.',
  'maintenance.submitting': 'Отправка сообщения...',
  'maintenance.sendingMsg': 'Отправляем ваше сообщение в техслужбу',
  'maintenance.myReports': 'Мои обращения',
  'maintenance.noReports': 'Обращений пока нет',
  'maintenance.reportNew': 'Сообщить о новой проблеме',
  'maintenance.reportAnother': 'Сообщить о другой проблеме',
  'maintenance.viewAll': 'Все обращения',
  'maintenance.reportStatus': 'Статус обращения',
  'maintenance.resolved': 'Решено',
  'maintenance.reportIssue': 'Сообщить о проблеме',
  'maintenance.subject': 'Тема',
  'maintenance.subjectPlaceholder': 'Краткое описание проблемы',
  'maintenance.details': 'Подробности (необязательно)',
  'maintenance.detailsPlaceholder': 'Опишите проблему подробнее...',
  'maintenance.location': 'Расположение (необязательно)',
  'maintenance.locationPlaceholder': 'Например: Ванная, возле окна',
  'maintenance.priority': 'Приоритет',
  'maintenance.priorityLow': 'Низкий',
  'maintenance.priorityLowDesc': 'Когда удобно',
  'maintenance.priorityNormal': 'Обычный',
  'maintenance.priorityNormalDesc': 'Стандартное время ответа',
  'maintenance.priorityHigh': 'Высокий',
  'maintenance.priorityHighDesc': 'Требует быстрого внимания',
  'maintenance.priorityUrgent': 'Срочный',
  'maintenance.priorityUrgentDesc': 'Требует немедленного внимания',
  'maintenance.submitReport': 'Отправить обращение',
  'maintenance.change': 'Изменить',
  'maintenance.customQuestion': 'Не нашли нужное?',
  'maintenance.reportCustom': 'Сообщить о другой проблеме',
  'maintenance.reported': 'Сообщено',
  'maintenance.oops': 'Упс!',
  // Welcome page
  'welcome.guests': 'Гостей',
  'welcome.yourStay': 'Ваше пребывание',
  'welcome.connectedDevices': 'Подключённые устройства',
  'welcome.shareDeviceDesc': 'Поделитесь доступом с другими гостями в номере. Каждый может использовать своё устройство.',
  'welcome.guidesDesc': 'Пошаговые руководства и информация',
  'welcome.addDevice': 'Добавить устройство',
  'welcome.processing': 'Обработка...',
  'welcome.availableServices': 'Доступные услуги',
  'welcome.shareModalTitle': 'Поделиться доступом',
  'welcome.shareModalDesc': 'Поделитесь ссылкой с другим гостем для доступа с его устройства.',
  'welcome.shareLink': 'Поделиться ссылкой',
  'welcome.copyLink': 'Скопировать ссылку',
  'welcome.copied': 'Скопировано!',
  'welcome.deviceSlots': 'Слотов устройств',
  'welcome.sessionExpired': 'Сессия истекла',
  'welcome.scanAgain': 'Отсканируйте QR-код ещё раз для доступа к услугам.',
  'welcome.welcome': 'Добро пожаловать',
  // ---- auto-added: full guest-app coverage (June 2026) ----
  'activity.active': 'Активные запросы',
  'activity.allPeople': 'Все',
  'activity.completed': 'Завершено',
  'activity.empty': 'Пока нет активности',
  'activity.emptySub': 'Здесь появятся ваши запросы и заказы',
  'activity.filter.all': 'Все',
  'activity.filter.housekeeping': 'Обслуживание номера',
  'activity.filter.maintenance': 'Сообщить о проблеме',
  'activity.filter.orders': 'Заказы',
  'activity.filter.reservations': 'Бронирования',
  'activity.filter.services': 'Услуги',
  'activity.filter.vouchers': 'Ваучеры',
  'activity.title': 'Моя активность',
  'activity.vouchers': 'Ваучеры',
  'common.category': 'Категория',
  'common.date': 'Дата',
  'common.hoursAgo': '{{n}} ч назад',
  'common.justNow': 'Только что',
  'common.minutesAgo': '{{n}} мин назад',
  'common.ok': 'OK',
  'common.oops': 'Упс!',
  'common.priority': 'Приоритет',
  'common.retry': 'Повторить',
  'common.room': 'Номер',
  'common.total': 'Итого',
  'folio.allCharges': 'Просмотр всех начислений за номер',
  'folio.balance': 'К оплате',
  'folio.changeCard': 'Изменить карту',
  'folio.charged': 'Списано',
  'folio.charges': 'Начисления',
  'folio.depositCaptured': 'Депозит удержан',
  'folio.depositHeld': 'Депозит заблокирован',
  'folio.depositReleased': 'Депозит возвращён',
  'folio.downloadInvoice': 'Скачать счёт',
  'folio.empty': 'Начислений пока нет',
  'folio.emptyHint': 'Здесь появятся начисления за заказы и услуги.',
  'folio.hideHistory': 'Скрыть историю',
  'folio.howToPay': 'Как оплатить',
  'folio.noCharges': 'Нет начислений',
  'folio.paid': 'Оплачено',
  'folio.pay': 'Оплатить',
  'folio.payAtCounter': 'Оплата на стойке',
  'folio.payAtCounterHint': 'Подойдите к стойке регистрации',
  'folio.payHint': 'Оплатите ваш счёт',
  'folio.payOnline': 'Оплатить онлайн',
  'folio.payOnlineHint': 'Безопасная оплата онлайн',
  'folio.paymentSuccess': 'Оплата прошла успешно',
  'folio.recentCharges': 'Последние начисления',
  'folio.room': 'Номер',
  'folio.roomCharges': 'Начисления за номер',
  'folio.saveCard': 'Сохранить карту',
  'folio.savedForPayments': 'Сохранено для будущих платежей',
  'folio.securePayment': 'Безопасная оплата',
  'folio.securityDeposit': 'Залоговый депозит',
  'folio.settleAll': 'Оплатить всё',
  'folio.settleSuccess': 'Счёт успешно оплачен',
  'folio.settled': 'Оплачен',
  'folio.payViaHotel': 'Расходы добавляются к счёту отеля. Оплата при выезде — картой, которая есть у отеля, или на стойке регистрации.',
  'folio.statusDue': 'К оплате — пожалуйста, оплатите на стойке регистрации',
  'folio.paidAtHotel': 'Оплачено в отеле',
  'folio.cardRequiredHint': 'Сохраните платёжную карту, чтобы заказывать платные позиции — оплата списывается с неё при заказе.',
  'folio.chargedAtPurchase': 'Платные позиции списываются с сохранённой карты при заказе.',
  'folio.pmsPosted': 'В счёте отеля',
  'folio.pmsPending': 'Добавляется в счёт отеля',
  'folio.pmsBeingChecked': 'Проверяется отелем',
  'folio.pmsFrontDesk': 'Оплачено на стойке регистрации',
  'station_order.saveCard': 'Сохранить карту',
  'folio.showHistory': 'Просмотр истории',
  'folio.title': 'Мой счёт',
  'folio.total': 'Итого',
  'folio.waitingForTap': 'Ожидание касания',
  'folio.waitingForTapDesc': 'Приложите карту к считывателю',
  'footer.dataDeletion': 'Удаление данных',
  'footer.privacy': 'Политика конфиденциальности',
  'footer.terms': 'Условия использования',
  'guides.finish': 'Завершить',
  'guides.guides': 'Гиды',
  'guides.iUnderstand': 'Понятно',
  'guides.next': 'Далее',
  'guides.nextIn': 'Далее',
  'guides.noCategories': 'Нет категорий',
  'guides.noGuides': 'Нет гидов',
  'guides.previous': 'Назад',
  'guides.steps': 'Шаги',
  'guides.subtitle': 'Гиды',
  'guides.title': 'Гиды',
  'laundry.acknowledgedMsg': 'Принято',
  'laundry.activeOrder': 'Активный заказ',
  'laundry.add': 'Добавить',
  'laundry.contactDesk': 'Обратитесь на стойку регистрации',
  'laundry.deliveredMsg': 'Доставлено',
  'laundry.each': 'за шт.',
  'laundry.estimatedProcessing': 'Примерная обработка',
  'laundry.estimatedReady': 'Примерная готовность',
  'laundry.expressDelivery': 'Экспресс-доставка',
  'laundry.expressFee': 'Сбор за экспресс',
  'laundry.expressMsg': 'Экспресс-услуга',
  'laundry.history': 'История',
  'laundry.hours': 'часов',
  'laundry.item': 'предмет',
  'laundry.items': 'предметов',
  'laundry.limitReached': 'Лимит достигнут',
  'laundry.newRequest': 'Новый запрос',
  'laundry.noOrders': 'Нет заказов',
  'laundry.notesPlaceholder': 'Особые инструкции...',
  'laundry.receivedMsg': 'Получено',
  'laundry.room': 'Номер',
  'laundry.sendingMsg': 'Отправка запроса...',
  'laundry.sentMsg': 'Отправлено',
  'laundry.specialInstructions': 'Особые инструкции',
  'laundry.submitOrder': 'Оформить заказ',
  'laundry.editOrder': 'Изменить заказ',
  'laundry.cancelOrder': 'Отменить заказ',
  'laundry.confirmCancel': 'Нажмите для подтверждения',
  'laundry.cancelFailed': 'Не удалось отменить заказ',
  'laundry.updateOrder': 'Обновить заказ',
  'laundry.editingOrder': 'Редактирование заказа',
  'laundry.editingNote': 'Измените позиции и нажмите «Обновить».',
  'laundry.submitted': 'Отправлено',
  'laundry.submitting': 'Отправка...',
  'laundry.subtotal': 'Подытог',
  'laundry.title': 'Прачечная',
  'laundry.total': 'Итого',
  'laundry.tryTomorrow': 'Попробуйте завтра',
  'laundry.unavailable': 'Недоступно',
  'laundry.unavailableMsg': 'Услуга недоступна',
  'laundry.viewBag': 'Посмотреть пакет',
  'laundry.viewHistory': 'Посмотреть историю',
  'laundry.yourBag': 'Ваш пакет',
  'laundry.yourOrder': 'Ваш заказ',
  'maintenance.descriptionTooShort': 'Описание должно содержать минимум 3 символа',
  'maintenance.room': 'Номер',
  'maintenance.subjectRequired': 'Укажите тему вашего обращения',
  'messages.empty': 'Нет сообщений',
  'messages.namePrompt.description': 'Это будет использовано для идентификации ваших заказов и запросов',
  'messages.namePrompt.placeholder': 'Введите ваше имя',
  'messages.namePrompt.submit': 'Отправить',
  'messages.namePrompt.title': 'Как вас зовут?',
  'messages.placeholder': 'Введите сообщение...',
  'messages.subtitle': 'Сообщения',
  'messages.title': 'Сообщения',
  'messages.whatsapp.changeNumber': 'Изменить номер',
  'messages.whatsapp.checkWhatsApp': 'Проверьте WhatsApp',
  'messages.whatsapp.codeSentViaSms': 'Код отправлен по SMS',
  'messages.whatsapp.codeSentViaWhatsApp': 'Код отправлен через WhatsApp',
  'messages.whatsapp.connectDescription': 'Подключите WhatsApp, чтобы получать сообщения',
  'messages.whatsapp.enterCode': 'Введите код',
  'messages.whatsapp.modalTitle': 'Подтвердите WhatsApp',
  'messages.whatsapp.phonePlaceholder': 'Введите ваш номер телефона',
  'messages.whatsapp.phoneRequired': 'Требуется телефон',
  'messages.whatsapp.sendVerificationCode': 'Отправить код подтверждения',
  'messages.whatsapp.sendViaSms': 'Отправить по SMS',
  'messages.whatsapp.skipForNow': 'Пропустить',
  'messages.whatsapp.useSmsInstead': 'Использовать SMS',
  'messages.whatsapp.useWhatsAppInstead': 'Использовать WhatsApp',
  'messages.whatsapp.verified': 'Подтверждено',
  'messages.whatsapp.verifiedDescription': 'WhatsApp подтверждён',
  'messages.whatsapp.verify': 'Подтвердить',
  'messages.whatsapp.verifyButton': 'Подтвердить',
  'namePrompt.continue': 'Продолжить',
  'namePrompt.description': 'Это будет использовано для идентификации ваших заказов',
  'namePrompt.placeholder': 'Введите ваше имя',
  'namePrompt.title': 'Как вас зовут?',
  'namePrompt.useBookingName': 'Использовать имя из брони:',
  'nav.account': 'Аккаунт',
  'documents.mine': 'Я (мой номер)',
  'documents.forWhom': 'Чей это документ?',
  'documents.moveTo': 'Переместить в…',
  'documents.moveFailed': 'Не удалось переместить документ.',
  'profile.bookedForOthers': 'Забронировано вами',
  'rooms.shareUnavailable': 'Доступ к этому номеру сейчас недоступен.',
  'rooms.myTitle': 'Мои номера',
  'rooms.myIntro': 'Эти номера входят в ваше бронирование и указаны с вашим телефоном или почтой. Поделитесь доступом к каждому номеру с его гостями или укажите для номера свои контакты — тогда гость получит свой доступ и свои баллы.',
  'rooms.noneLeft': 'Теперь у каждого номера в бронировании свой гость.',
  'rooms.otherBooking': 'Этот номер в другом бронировании с вашими контактами — укажите для него свои.',
  'rooms.shareHint': 'Пусть гости номера отсканируют код, или отправьте им ссылку, чтобы открыть на своём телефоне.',
  'rooms.copied': 'Ссылка скопирована',
  'rooms.sendLink': 'Отправить ссылку',
  'rooms.share': 'Поделиться доступом',
  'rooms.shareAfterCheckIn': 'Доступом можно поделиться после заселения номера.',
  'rooms.ownContact': 'Свои контакты для номера',
  'rooms.bill': 'Счёт',
  'rooms.shareText': 'Ваш доступ к номеру {{room}}',
  'rooms.cardTitleManaged': 'Ваши номера',
  'rooms.cardBodyManaged': 'Ещё {{n}} номер(а) в вашем бронировании. Поделитесь доступом с их гостями или укажите для каждого свои контакты, чтобы они получали свои баллы.',
  'rooms.cardButtonManaged': 'Мои номера',
  'profile.managedRoom': 'Этот номер входит в бронирование {{booker}}. Чтобы получать баллы за своё проживание, попросите {{booker}} или стойку регистрации указать для номера ваш телефон.',
  'rooms.title': 'Зарегистрируйте номера',
  'rooms.cardTitle': 'Ваши другие номера',
  'rooms.cardBody': 'Ещё {{n}} номер(а) в этом бронировании указаны с вашим телефоном или почтой. Укажите, кто живёт в каждом, чтобы у каждого номера был свой доступ.',
  'rooms.cardButton': 'Зарегистрировать номера',
  'rooms.room': 'Номер',
  'rooms.loadError': 'Не удалось загрузить номера.',
  'rooms.saveError': 'Не удалось сохранить. Проверьте данные.',
  'rooms.doneTitle': 'Спасибо!',
  'rooms.doneBody': 'Теперь у каждого номера свой гость. Каждый получит свой доступ к проживанию.',
  'rooms.none': 'Нет номеров для регистрации.',
  'rooms.verifyFirst': 'Подтвердите номер телефона на этом устройстве, чтобы зарегистрировать номера.',
  'rooms.intro': 'Эти номера указаны с вашим телефоном или почтой. Укажите, кто живёт в каждом, и его телефон или почту, чтобы у каждого номера был свой доступ.',
  'rooms.guestName': 'Имя гостя',
  'rooms.noContact': 'Без телефона и почты (например, ребёнок) — я позабочусь об этом номере',
  'rooms.save': 'Сохранить',
  'profile.title': 'Мой профиль',
  'profile.loadError': 'Не удалось загрузить профиль.',
  'profile.joinError': 'Не удалось вступить. Попробуйте ещё раз.',
  'profile.joinTitle': 'Вступите в {{chain}} Rewards',
  'profile.joinBody': 'Получайте {{points}} баллов за каждую ночь в любом из наших отелей. Ваши проживания и баллы хранятся в профиле.',
  'profile.unavailable': 'Программа лояльности недоступна в этом отеле.',
  'profile.verifyFirst': 'Подтвердите номер телефона на этом устройстве, чтобы вступить.',
  'profile.phone': 'Телефон',
  'profile.email': 'Эл. почта',
  'profile.marketing': 'Присылайте мне предложения и новости (необязательно)',
  'profile.consent': 'Вступая, вы соглашаетесь, что мы храним ваш профиль, проживания и баллы, пока вы не попросите их удалить.',
  'profile.join': 'Вступить',
  'profile.notNow': 'Не сейчас',
  'profile.points': 'баллов',
  'profile.memberSince': 'Участник с {{date}}',
  'profile.details': 'Мои данные',
  'profile.name': 'Имя',
  'profile.stays': 'Мои проживания',
  'profile.noStays': 'Проживаний пока нет.',
  'profile.nights': 'Ночей: {{n}}',
  'profile.history': 'История баллов',
  'profile.noPoints': 'Баллы появятся здесь после каждого проживания.',
  'profile.earned': 'Проживание',
  'profile.adjusted': 'Корректировка',
  'profile.onReservationOf': 'В бронировании: {{name}}',
  'nav.activity': 'Моя активность',
  'nav.guides': 'Гиды',
  'nav.housekeeping': 'Обслуживание номера',
  'nav.maintenance': 'Сообщить о проблеме',
  'nav.menu': 'Меню',
  'nav.messages': 'Сообщения',
  'nav.notifications': 'Уведомления',
  'nav.vouchers': 'Ваучеры',
  'payment.backToFolio': 'Вернуться к счёту',
  'payment.cancelled': 'Платёж отменён',
  'payment.cancelledMessage': 'Ваш платёж был отменён',
  'payment.cardSavedAuto': 'Карта сохранена автоматически',
  'payment.failed': 'Платёж не прошёл',
  'payment.failedMessage': 'Ваш платёж не прошёл',
  'payment.success': 'Платёж выполнен',
  'payment.successMessage': 'Ваш платёж выполнен успешно',
  'payment.tryAgain': 'Попробовать снова',
  'station_order.add': 'Добавить',
  'station_order.outOfStock': 'Нет в наличии',
  'station_order.allDone': 'На сегодня всё',
  'station_order.closed': 'Закрыто',
  'station_order.closedHours': 'Закрыто в эти часы',
  'station_order.enjoy': 'Приятного аппетита',
  'station_order.enterTable': 'Введите номер стола',
  'station_order.folioChargeHint': 'Добавить на счёт номера',
  'station_order.folioChargeNote': 'Это будет начислено на ваш номер',
  'station_order.folioChargeTitle': 'Начислить на номер',
  'station_order.included': 'Включено',
  'station_order.maxOrdersReached': 'Достигнут лимит заказов',
  'station_order.maxOrdersReachedRoom': 'Достигнут лимит заказов для номера',
  'station_order.nextShift': 'Следующая смена',
  'station_order.nextShiftNote': 'Доступно в следующую смену',
  'station_order.notes': 'Примечания',
  'station_order.notesPlaceholder': 'Особые пожелания...',
  'station_order.oops': 'Упс!',
  'station_order.orderFailed': 'Не удалось оформить заказ',
  'station_order.ordered': 'Заказано',
  'station_order.placeAnother': 'Сделать ещё заказ',
  'station_order.placeOrder': 'Оформить заказ',
  'station_order.editOrder': 'Изменить заказ',
  'station_order.cancelOrder': 'Отменить заказ',
  'station_order.confirmCancel': 'Нажмите для подтверждения',
  'station_order.cancelFailed': 'Не удалось отменить заказ',
  'station_order.updateOrder': 'Обновить заказ',
  'station_order.editingOrder': 'Редактирование заказа',
  'station_order.editingNote': 'Измените позиции и нажмите «Обновить заказ».',
  'station_order.preparing': 'Готовится',
  'station_order.readyPickup': 'Готово к выдаче',
  'station_order.readyRoom': 'Готово в номере',
  'station_order.readyTable': 'Готово на столе',
  'station_order.selectItems': 'Выберите блюда',
  'station_order.sending': 'Отправка',
  'station_order.table': 'Стол',
  'station_order.tableNumber': 'Номер стола',
  'station_order.thankYou': 'Спасибо',
  'station_order.unableLoad': 'Не удалось загрузить',
  'station_order.yourOrder': 'Ваш заказ',
  'status_card.editOrder': 'Изменить заказ',
  'status_card.express': 'Экспресс',
  'status_card.expressSurcharge': 'Доплата за экспресс',
  'status_card.guest': 'гость',
  'status_card.guests': 'гостей',
  'status_card.hk.itemRequest': 'Запрос предметов',
  'status_card.hk.roomCleaning': 'Уборка номера',
  'status_card.hk.turndown': 'Вечерняя подготовка номера',
  'status_card.issue': 'Проблема',
  'status_card.item': 'Предмет',
  'status_card.partySize': 'Количество гостей',
  'status_card.quotedPrice': 'Указанная цена',
  'status_card.reason': 'Причина',
  'status_card.request': 'Запрос',
  'status_card.scheduled': 'Запланировано',
  'status_card.service': 'Услуга',
  'status_card.station': 'Станция',
  'status_card.step.acknowledged': 'Принято',
  'status_card.step.cancelled': 'Отменён',
  'status_card.step.completed': 'Завершено',
  'status_card.step.confirmed': 'Подтверждён',
  'status_card.step.declined': 'Отклонён',
  'status_card.step.delivered': 'Доставлен',
  'status_card.step.inProgress': 'В работе',
  'status_card.step.pending': 'Ожидает',
  'status_card.step.preparing': 'Готовится',
  'status_card.step.ready': 'Готов',
  'status_card.step.received': 'Получено',
  'status_card.step.reported': 'Сообщено',
  'status_card.step.resolved': 'Решено',
  'status_card.step.sent': 'Отправлено',
  'status_card.step.started': 'Начато',
  'status_card.summary.laundry': 'Прачечная',
  'status_card.summary.maintenance': 'Сообщить о проблеме',
  'status_card.summary.order': 'Заказ',
  'status_card.summary.request': 'Запрос',
  'status_card.summary.reservation': 'Бронирование',
  'status_card.summary.service': 'Запрос услуги',
  'status_card.svc.concierge': 'Консьерж',
  'status_card.svc.informationRequest': 'Информационный запрос',
  'status_card.svc.luggage': 'Помощь с багажом',
  'status_card.svc.other': 'Другое',
  'status_card.svc.roomService': 'Обслуживание номера',
  'status_card.svc.specialRequest': 'Особый запрос',
  'status_card.svc.taxi': 'Такси / Поездка',
  'status_card.svc.transport': 'Транспорт',
  'status_card.svc.wakeUpCall': 'Звонок-будильник',
  'status_card.type': 'Тип',
  'status_card.type.housekeeping': 'Обслуживание номера',
  'status_card.type.laundry': 'Прачечная',
  'status_card.type.maintenance': 'Сообщить о проблеме',
  'status_card.type.order': 'Заказ еды',
  'status_card.type.reservation': 'Бронирование',
  'status_card.type.service': 'Запрос услуги',
  'voucher.expires': 'Истекает',
  'voucher.fixedValue': 'номинал {{amount}}',
  'voucher.freeItem': 'Бесплатная позиция',
  'voucher.percentOff': 'скидка {{amount}}%',
  'voucher.redeemed': 'Использован',
  'voucher.statusActive': 'Активен',
  'voucher.statusExpired': 'Истёк',
  'voucher.statusFullyUsed': 'Полностью использован',
  'voucher.usedCount': 'использовано {{used}} / {{max}}',
  'vouchers.available': 'Доступно',
  'vouchers.expires': 'Истекает',
  'vouchers.noVouchers': 'Нет ваучеров',
  'vouchers.noVouchersDesc': 'Нет доступных ваучеров',
  'vouchers.subtitle': 'Мои ваучеры',
  'vouchers.title': 'Мои ваучеры',
  'vouchers.used': 'Использованы / Истёкшие',
  'vouchers.uses': 'Использований',
  'vouchers.validAt': 'Действителен в',
  'welcome.dayPass': 'Дневной пропуск',
  'welcome.event': 'Мероприятие',
  'welcome.lounge': 'Лаундж',
  'welcome.orderNow': 'Нажмите, чтобы открыть меню и заказать',
  'welcome.ordering': 'Заказ',
  'welcome.poolbar': 'Бар у бассейна',
  'welcome.services': 'Услуги',
  'welcome.sessionLabel': 'Сессия',
  'welcome.spaSession': 'Сессия',
  'welcome.table': 'Стол',
  'welcome.verifyDesc': 'Подтвердите ваш телефон, чтобы разблокировать начисления на счёт и подключить дополнительные устройства к сессии.',
  'welcome.verifyNow': 'Подтвердить',
  'welcome.verifyRoom': 'Подтвердите ваш номер',

  // One session per stay: pre-arrival + post-stay modules
  'common.saving': 'Сохранение…',
  'common.continue': 'Продолжить',
  'common.backHome': 'На главную',
  'common.done': 'Готово',
  'module.registration': 'Регистрация',
  'module.documents': 'Документы',
  'module.review': 'Отзыв',
  'module.lost_property': 'Забытые вещи',
  'welcome.registrationDesc': 'Ваши данные и подпись до приезда',
  'welcome.documentsDesc': 'Загрузите паспорт или удостоверение',
  'welcome.beforeYouArrive': 'До приезда',
  'welcome.arrivingOn': 'Заезд',
  'welcome.registrationDone': 'Регистрация завершена. До скорой встречи!',
  'welcome.completeRegistration': 'Завершите регистрацию',
  'welcome.reviewDesc': 'Расскажите, как прошло ваше пребывание',
  'welcome.lostPropertyDesc': 'Что-то забыли?',
  'welcome.thanksForStaying': 'Спасибо, что остановились у нас',
  'welcome.postStayHint': 'Счёт, сообщения и забытые вещи доступны здесь ещё несколько дней.',
  'welcome.rateStay': 'Оцените пребывание',
  'registration.title': 'Регистрация',
  'registration.subtitle': 'Ваши данные для проживания',
  'registration.nameRequired': 'Укажите имя и фамилию.',
  'registration.saveFailed': 'Не удалось сохранить данные.',
  'registration.acceptTerms': 'Примите условия, чтобы продолжить.',
  'registration.confirmFailed': 'Не удалось завершить регистрацию.',
  'registration.firstName': 'Имя',
  'registration.lastName': 'Фамилия',
  'registration.name': 'Имя',
  'registration.email': 'Эл. почта',
  'registration.phone': 'Телефон',
  'registration.country': 'Страна',
  'registration.passportId': 'Паспорт / удостоверение',
  'registration.dateOfBirth': 'Дата рождения',
  'registration.party': 'Гости',
  'registration.adults': 'Взрослые',
  'registration.children': 'Дети',
  'registration.babies': 'Младенцы',
  'registration.signature': 'Подпись (введите полное имя)',
  'registration.terms': 'Подтверждаю, что данные верны, и принимаю условия отеля.',
  'registration.complete': 'Завершить регистрацию',
  'registration.doneTitle': 'Всё готово',
  'registration.doneText': 'Регистрация завершена. Мы всё подготовим к вашему приезду.',
  'registration.addDocuments': 'Загрузить документы',
  'registration.edit': 'Изменить данные',
  'documents.title': 'Документы',
  'documents.subtitle': 'Паспорт или удостоверение для каждого гостя',
  'documents.hint': 'JPG, PNG или PDF, до 15 МБ.',
  'documents.passport': 'Паспорт',
  'documents.idCard': 'Удостоверение личности',
  'documents.visa': 'Виза',
  'documents.other': 'Другое',
  'documents.add': 'Добавить файл',
  'documents.uploading': 'Загрузка…',
  'documents.uploadFailed': 'Не удалось загрузить.',
  'documents.removeFailed': 'Не удалось удалить документ.',
  'documents.none': 'Документы ещё не загружены.',
  'documents.verified': 'проверен',
  'documents.view': 'Открыть',
  'documents.locked': 'заблокирован',
  'documents.remove': 'Удалить',
  'review.title': 'Оцените пребывание',
  'review.subtitle': 'Спасибо, что остановились у нас',
  'review.overall': 'В целом',
  'review.staff': 'Персонал',
  'review.cleanliness': 'Чистота',
  'review.comment': 'Хотите что-нибудь нам сказать?',
  'review.send': 'Отправить отзыв',
  'review.update': 'Обновить отзыв',
  'review.thanks': 'Спасибо! Ваш отзыв отправлен.',
  'review.empty': 'Поставьте оценку или оставьте комментарий.',
  'review.failed': 'Не удалось отправить отзыв. Попробуйте ещё раз.',
  'lostProperty.title': 'Забытые вещи',
  'lostProperty.subtitle': 'Что-то забыли? Сообщите нам',
  'lostProperty.item': 'Что вы забыли?',
  'lostProperty.description': 'Описание (цвет, марка…)',
  'lostProperty.where': 'Где, по-вашему, вы его оставили?',
  'lostProperty.contact': 'Как с вами связаться (необязательно)',
  'lostProperty.send': 'Отправить',
  'lostProperty.itemRequired': 'Сообщите, что вы забыли.',
  'lostProperty.failed': 'Не удалось отправить. Попробуйте ещё раз.',
  'lostProperty.sentTitle': 'Мы получили ваше сообщение',
  'lostProperty.sentText': 'Мы поищем и ответим в сообщениях.',
  'lostProperty.openMessages': 'Открыть сообщения',
};

// All translations indexed by language code
export const translations: Record<string, Translations> = {
  en,
  he,
  ar,
  ru,
};

// Supported languages configuration
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' as const },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', direction: 'rtl' as const },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' as const },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' as const },
];

// Default language
export const DEFAULT_LANGUAGE = 'en';

/**
 * Get local fallback translation
 * Used when API translations are not available
 */
export function getLocalTranslation(key: string, language: string): string | null {
  const langTranslations = translations[language] || translations['en'];
  return langTranslations[key] || translations['en'][key] || null;
}

/**
 * Get language direction
 */
export function getLanguageDirection(code: string): 'ltr' | 'rtl' {
  const lang = supportedLanguages.find(l => l.code === code);
  return lang?.direction || 'ltr';
}
